import React, { useState, useEffect, useRef } from "react";
import { 
  Terminal, 
  User, 
  Cpu, 
  Copy, 
  Check, 
  FileText, 
  Download, 
  CheckCircle2, 
  Wrench,
  RefreshCw,
  ExternalLink,
  Flame,
  UserCheck,
  Lock,
  BookOpen,
  Package,
  MessageSquare,
  TrendingUp,
  Palette,
  Briefcase,
  Sun,
  Moon,
  ChevronDown,
  FolderOpen,
  Trash2,
  AlertTriangle,
  X,
  Save,
  StickyNote
} from "lucide-react";
import { ChatMessage, AppMode } from "../types";

type UiTheme = "rodgerspc" | "light" | "dark";

interface SavedChatFolder {
  id: string;
  name: string;
  builtIn?: boolean;
  mode?: AppMode;
  createdAt?: string;
}

interface SavedChatRecord {
  id: string;
  title: string;
  folderId: string;
  mode?: AppMode;
  messages?: ChatMessage[];
  createdAt?: string;
  updatedAt?: string;
}

interface ChatWorkspaceProps {
  messages: ChatMessage[];
  currentMode: AppMode;
  onModeChange: (mode: AppMode) => void;
  /**
   * Focused Drive telemetry:
   * - activeContextCount means checked/gold focused Drive files available to use.
   * - totalContextCharacters means content already loaded and available.
   */
  activeContextCount: number;
  totalContextCharacters: number;
  driveScopeCount?: number;
  loadedContextCount?: number;
  manualContextCount?: number;
  pinnedContextCount?: number;
  treeItemCount?: number;
  loading: boolean;
  requestProgressSteps?: string[];
  driveSyncEnabled?: boolean;
  driveSidebarVisible?: boolean;
  onToggleDriveSidebar?: () => void;
  onOpenExporter?: () => void;
  onCreateNotes?: () => void;
  savedChatFolders?: SavedChatFolder[];
  savedChats?: SavedChatRecord[];
  activeSavedChatId?: string | null;
  defaultSavedChatFolderId?: string;
  suggestedSavedChatTitle?: string;
  onSaveCurrentChatToFolder?: (folderId: string, title?: string) => void;
  onCreateSavedChatFolder?: (folderName: string) => SavedChatFolder | null;
  globalTheme: UiTheme;
  onChangeGlobalTheme: (theme: UiTheme) => void;
  chatTheme: UiTheme;
  onChangeChatTheme: (theme: UiTheme) => void;
  inputTheme?: UiTheme;
  onChangeInputTheme?: (theme: UiTheme) => void;
  inputPanelVisible?: boolean;
  onToggleInputPanel?: () => void;
  onClearChat?: () => void;
  onUpdateMessage?: (id: string, newContent: string) => void;
  onUpdateReport?: (msgId: string, reportId: string, newText: string) => void;
  dbLoaded?: boolean;
  hasUnclearedChat?: boolean;
  onContinueLastChat?: () => void;
  onClearLastChatBackup?: () => void;
  lastChatBackupClean?: boolean;
  lastSavedChatCount?: number;
  onRetryMessage?: (failedAssistantMessageId: string) => void;
  onCancelRequest?: () => void;
}


function formatModeLabel(mode: AppMode | string | undefined): string {
  const raw = String(mode || "");
  const normalized = raw.toLowerCase().replace(/\s+/g, "_");

  const labels: Record<string, string> = {
    tech_assistant: "Bench Tech",
    tech_bench: "Bench Tech",
    technician_assistant: "Bench Tech",
    bench_tech: "Bench Tech",
    blog_engine: "Blog Engine",
    personal_vault: "Personal Vault",
    inventory_scout: "Inventory Scout",
    lead_responder: "Lead Responder",
    seo_listings: "SEO & Listings",
    web_creative: "Web Creative",
    web_creative_hub: "Web Creative",
    front_desk: "Front Desk",
  };

  return labels[normalized] || raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getMessageModeLabel(msg: ChatMessage, fallbackMode: AppMode): string {
  const typed = msg as ChatMessage & { modeAtSend?: AppMode; newMode?: AppMode; prevMode?: AppMode };

  if (typed.modeAtSend) return formatModeLabel(typed.modeAtSend);
  if (typed.newMode) return formatModeLabel(typed.newMode);
  return formatModeLabel(fallbackMode);
}

function isFakeOrTruncatedVisualPayload(value: unknown): boolean {
  const raw = String(value || "");

  return (
    !raw ||
    raw.includes("[Payload size optimized]") ||
    raw.includes("Payload size optimized") ||
    raw.includes("...[Payload") ||
    raw.includes("... [Payload") ||
    raw.includes("Attachment metadata only in model payload") ||
    raw.includes("not stored in cloud backup") ||
    raw.includes("restored without full media payload")
  );
}

function parseRenderableDataUrl(value: unknown): { mimeType: string; dataUrl: string } | null {
  const raw = String(value || "").trim();

  if (isFakeOrTruncatedVisualPayload(raw)) return null;

  const match = raw.match(/^data:([^;]+);base64,([A-Za-z0-9+/=\s]+)$/);
  if (!match?.[1] || !match?.[2]) return null;

  const mimeType = match[1].trim().toLowerCase();
  const base64 = match[2].replace(/\s+/g, "");

  if (base64.length < 40) return null;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return null;

  return {
    mimeType,
    dataUrl: `data:${mimeType};base64,${base64}`,
  };
}

function isRenderableDataUrl(value: unknown): boolean {
  return Boolean(parseRenderableDataUrl(value));
}

function getRenderableAttachmentDataUrl(att: ChatMessage["attachments"] extends Array<infer T> ? T : any): string {
  const fromBase64 = parseRenderableDataUrl(att?.base64);
  if (fromBase64?.mimeType.startsWith("image/")) return fromBase64.dataUrl;

  const fromContent = parseRenderableDataUrl(att?.content);
  if (fromContent?.mimeType.startsWith("image/")) return fromContent.dataUrl;

  return "";
}

function isRenderableRemoteImageSrc(value: unknown): boolean {
  const raw = String(value || "").trim();
  if (isFakeOrTruncatedVisualPayload(raw)) return false;
  return raw.startsWith("blob:") || /^https?:\/\//i.test(raw);
}

function getRenderableMessageVisualUrl(msg: ChatMessage): string {
  const imageAsset = (msg as ChatMessage & {
    imageAsset?: { dataUrl?: string; url?: string; mimeType?: string };
  }).imageAsset;

  const assetData = parseRenderableDataUrl(imageAsset?.dataUrl);
  if (assetData?.mimeType.startsWith("image/")) return assetData.dataUrl;

  if (isRenderableRemoteImageSrc(imageAsset?.url)) {
    return String(imageAsset?.url || "").trim();
  }

  const directPayload = parseRenderableDataUrl(msg.imageUrl);
  if (directPayload && (directPayload.mimeType.startsWith("image/") || directPayload.mimeType === "application/pdf")) {
    return directPayload.dataUrl;
  }

  if (isRenderableRemoteImageSrc(msg.imageUrl)) {
    return String(msg.imageUrl || "").trim();
  }

  return "";
}

/**
 * Validates links appearing in chatbot outputs against /api/validate-links on rendering.
 */
function LinkValidatedContent({ text }: { text: string }) {
  const renderParts = () => {
    const source = text || "";
    const regex = /(https?:\/\/[^\s)\u00a0]+)/g;
    const pieces: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = regex.exec(source)) !== null) {
      const rawUrl = match[0].replace(/[.,;:?]$/, "").replace(/\)$/, "");
      const start = match.index;
      const end = start + match[0].length;

      if (start > lastIndex) {
        pieces.push(<span key={`txt-${key++}`}>{source.slice(lastIndex, start)}</span>);
      }

      pieces.push(
        <a
          key={`url-${key++}`}
          href={rawUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-400 hover:text-sky-300 font-bold underline decoration-sky-400/50 underline-offset-2 inline-flex items-center gap-0.5 break-all"
        >
          {rawUrl}
          <ExternalLink className="w-2.5 h-2.5 shrink-0" />
        </a>
      );

      lastIndex = end;
    }

    if (lastIndex < source.length) {
      pieces.push(<span key={`txt-${key++}`}>{source.slice(lastIndex)}</span>);
    }

    return pieces;
  };

  return <>{renderParts()}</>;
}

/**
 * Highly styled terminal command card block with Copy buttons, shell types, and warning block integration
 */
function RenderedCodeBlock({
  code,
  language,
  isLightTheme
}: {
  code: string;
  language: string;
  isLightTheme: boolean;
  key?: string;
}) {
  const [copied, setCopied] = useState(false);

  const rawLines = (code || "").replace(/\r\n/g, "\n").split("\n");

  let terminal = "";
  let runMode: "Single" | "Batch" = "Single";
  let isRisky = false;
  let warningMessage = "";
  let scope = "";
  let restartNeeded = false;
  let adminRequired = false;
  let description = "";

  const lang = (language || "").trim().toLowerCase();

  if (["powershell", "ps1", "pwsh"].includes(lang)) {
    terminal = "PowerShell";
  } else if (["cmd", "bat", "batch"].includes(lang)) {
    terminal = "CMD";
  } else if (["reg", "registry"].includes(lang)) {
    terminal = "Registry";
  } else if (["bash", "sh"].includes(lang)) {
    terminal = "Bash";
  } else if (["macos", "zsh"].includes(lang)) {
    terminal = "macOS Terminal";
  } else if (lang === "run") {
    terminal = "Run";
  }

  rawLines.forEach((line) => {
    const cleanLine = line.trim();
    const match = cleanLine.match(/^(?:#|REM|;|\/\/)\s*([A-Z_]+):\s*(.*)$/i);

    if (match) {
      const key = match[1].toLowerCase();
      const val = match[2].trim();

      if (key === "terminal" || key === "shell") terminal = val;
      if (key === "type" || key === "runmode" || key === "mode") {
        runMode = val.toLowerCase().includes("batch") || val.toLowerCase().includes("together") ? "Batch" : "Single";
      }
      if (key === "risky") isRisky = ["true", "1", "yes"].includes(val.toLowerCase());
      if (key === "warning") warningMessage = val;
      if (key === "scope" || key === "impact") scope = val;
      if (key === "restart") restartNeeded = ["true", "1", "yes"].includes(val.toLowerCase());
      if (key === "admin") adminRequired = ["true", "1", "yes", "required"].includes(val.toLowerCase());
      if (key === "desc" || key === "description") description = val;
    }
  });

  const displayCode = rawLines
    .filter((line) => {
      const cleanLine = line.trim();
      return !cleanLine.match(/^(?:#|REM|;|\/\/)\s*(?:TERMINAL|SHELL|TYPE|RUNMODE|MODE|RISKY|WARNING|SCOPE|IMPACT|RESTART|ADMIN|DESC|DESCRIPTION|RESTORE):/i);
    })
    .join("\n")
    .trim();

  const executableLines = displayCode
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#") && !line.startsWith("//") && !line.toLowerCase().startsWith("rem "));

  const lowerCode = displayCode.toLowerCase();

  if (!terminal) {
    const looksLikeBash =
      lowerCode.includes("sudo ") ||
      lowerCode.includes("lsblk") ||
      lowerCode.includes("blkid") ||
      lowerCode.includes("fdisk") ||
      lowerCode.includes("mount ") ||
      lowerCode.includes("umount") ||
      lowerCode.includes("rsync") ||
      lowerCode.includes("ddrescue") ||
      lowerCode.includes("dmesg") ||
      lowerCode.includes("findmnt") ||
      lowerCode.includes("/mnt/") ||
      lowerCode.includes("/dev/") ||
      lowerCode.includes("mkdir -p") ||
      lowerCode.includes("ctrl + c");

    if (looksLikeBash) {
      terminal = "Bash";
    } else if (lowerCode.includes("get-") || lowerCode.includes("set-") || lowerCode.includes("remove-") || lowerCode.includes("powershell")) {
      terminal = "PowerShell";
    } else if (lowerCode.includes("dism ") || lowerCode.includes("sfc ") || lowerCode.includes("chkdsk ") || lowerCode.includes("ipconfig") || lowerCode.includes("netsh") || lowerCode.includes("net user") || lowerCode.includes("net localgroup")) {
      terminal = "CMD";
    } else {
      terminal = "Command";
    }
  }

  if (
    lowerCode.includes("rmdir /s") ||
    lowerCode.includes("del /f") ||
    lowerCode.includes("format ") ||
    lowerCode.includes("diskpart") ||
    lowerCode.includes("clean") && lowerCode.includes("diskpart") ||
    lowerCode.includes("reg delete") ||
    lowerCode.includes("remove-item") ||
    lowerCode.includes("clear-disk") ||
    lowerCode.includes("reset-physicaldisk") ||
    lowerCode.includes("bcdedit") ||
    lowerCode.includes("bootrec") ||
    lowerCode.includes("ddrescue -f") ||
    lowerCode.includes("mkfs") ||
    lowerCode.includes("wipefs") ||
    lowerCode.includes("sgdisk") ||
    lowerCode.includes("parted") ||
    lowerCode.includes("fdisk ") ||
    lowerCode.includes("rm -rf") ||
    lowerCode.includes("rsync --delete")
  ) {
    isRisky = true;
  }

  if (
    lowerCode.includes("dism ") ||
    lowerCode.includes("sfc ") ||
    lowerCode.includes("chkdsk") ||
    lowerCode.includes("netsh") ||
    lowerCode.includes("net user") ||
    lowerCode.includes("net localgroup") ||
    lowerCode.includes("bcdedit") ||
    lowerCode.includes("bootrec") ||
    lowerCode.includes("get-physicaldisk") ||
    lowerCode.includes("get-disk") ||
    lowerCode.includes("repair-volume") ||
    lowerCode.includes("sudo ") ||
    lowerCode.includes("ddrescue") ||
    lowerCode.includes("mount ") ||
    lowerCode.includes("umount") ||
    lowerCode.includes("fdisk") ||
    lowerCode.includes("blkid")
  ) {
    adminRequired = true;
  }

  if (lowerCode.includes("shutdown /r") || lowerCode.includes("restart-computer") || lowerCode.includes("netsh winsock reset")) {
    restartNeeded = true;
  }

  if (executableLines.length > 1 && runMode === "Single") {
    runMode = "Batch";
  }

  const shellLabel = `${terminal}${adminRequired ? " Admin" : ""}`;
  const metaNotes = [
    runMode === "Batch" ? "Run together" : "",
    restartNeeded ? "Restart likely" : "",
    description,
  ].filter(Boolean);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="my-3 font-sans max-w-full text-left">
      {isRisky && (
        <div className={`mb-2 rounded-xl border px-3 py-2.5 text-[11px] leading-relaxed ${
          isLightTheme
            ? "border-rose-200 bg-rose-50 text-rose-800"
            : "border-rose-400/25 bg-rose-950/20 text-rose-100"
        }`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
            <div>
              <div className="font-semibold">Warning before running</div>
              <div>{warningMessage || "This command can change storage, boot, registry, system state, or customer data. Confirm the exact target device/path and backup status before running."}</div>
              {scope && <div className="mt-1 font-mono text-[10px] opacity-75">Scope: {scope}</div>}
            </div>
          </div>
        </div>
      )}

      <div className={`rpc-code-card overflow-hidden rounded-2xl border shadow-sm ${
        isLightTheme
          ? "border-slate-200 bg-white"
          : "border-white/10 bg-[#0B1220]"
      }`}>
        <div className={`rpc-code-card-header flex items-center justify-between gap-3 border-b px-3 py-1.5 ${
          isLightTheme
            ? "border-slate-200 bg-slate-50/90"
            : "border-white/10 bg-white/[0.035]"
        }`}>
          <div className="flex min-w-0 items-center gap-2">
            <span className={`rpc-shell-badge inline-flex items-center rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.11em] shadow-sm ${
              isRisky
                ? isLightTheme
                  ? "border-rose-300 bg-gradient-to-b from-rose-50 to-white text-rose-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)]"
                  : "border-rose-400/35 bg-gradient-to-b from-rose-500/14 to-[#08101f] text-rose-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                : isLightTheme
                  ? "border-[#143F8F]/30 bg-gradient-to-b from-white to-slate-100 text-[#143F8F] shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_6px_16px_rgba(20,63,143,0.07)]"
                  : "border-[#F6A400]/55 bg-gradient-to-b from-[#122444]/96 to-[#071226]/96 text-[#FFB72B] shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_8px_20px_rgba(246,164,0,0.08)]"
            }`}>
              {shellLabel}
            </span>
            {metaNotes.length > 0 && (
              <span className={`truncate text-[10.5px] ${isLightTheme ? "text-slate-500" : "text-slate-400"}`}>
                {metaNotes.join(" · ")}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[9.5px] font-semibold transition ${
              isLightTheme
                ? "border-slate-200 bg-white text-slate-600 hover:border-[#143F8F]/30 hover:text-[#143F8F]"
                : "border-white/10 bg-white/[0.04] text-slate-300 hover:border-[#F6A400]/35 hover:text-[#F6A400]"
            }`}
            title="Copy this command"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" /> Copy
              </>
            )}
          </button>
        </div>

        <div className={`rpc-code-body overflow-x-auto px-3.5 py-2.5 ${
          isLightTheme ? "bg-white" : "bg-[#070D18]"
        }`}>
          <pre className={`rpc-command-pre select-all whitespace-pre overflow-x-auto font-mono text-[11.5px] leading-5 ${
            isLightTheme ? "text-slate-900" : "text-slate-100"
          }`}>{displayCode}</pre>
        </div>
      </div>
    </div>
  );
}



/**
 * Premium markdown parser styled exactly like the provided screenshot
 */
function PremiumMarkdownContent({ text, isLightTheme }: { text: string; isLightTheme: boolean }) {
  const [copiedInlineCode, setCopiedInlineCode] = useState<string | null>(null);

  const handleCopyInlineCode = async (
    event: React.MouseEvent<HTMLButtonElement>,
    value: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const copyValue = String(value || "").trim();
    if (!copyValue) return;

    try {
      await navigator.clipboard.writeText(copyValue);
      setCopiedInlineCode(copyValue);
      window.setTimeout(() => setCopiedInlineCode(null), 1600);
    } catch (err) {
      console.error("Failed to copy inline suggestion:", err);
    }
  };

  // Normalize escaped newlines and line break formats
  const normalizedText = text
    ? text
        .replace(/\\n/g, "\n")
        .replace(/\r\n/g, "\n")
    : "";

  const lines = normalizedText.split("\n");
  const parsedElements: React.ReactNode[] = [];
  
  let currentListItems: React.ReactNode[] = [];
  let currentTableLines: string[] = [];
  let listKey = 0;

  const flushList = () => {
    if (currentListItems.length > 0) {
      const items = [...currentListItems];
      parsedElements.push(
        <ul key={`list-${listKey++}`} className="list-none pl-1 space-y-1.5 my-3">
          {items}
        </ul>
      );
      currentListItems = [];
    }
  };

  const flushTable = () => {
    if (currentTableLines.length > 0) {
      const rows = currentTableLines.map(line => {
        return line.split("|").map(cell => cell.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      });
      
      const dataRows = rows.filter(row => !row.every(cell => cell.match(/^[-:\s]+$/)));
      
      if (dataRows.length > 0) {
        const headerRow = dataRows[0];
        const bodyRows = dataRows.slice(1);
        
        parsedElements.push(
          <div key={`table-wrapper-${listKey++}`} className="overflow-x-auto w-full my-4 border rounded-xl border-slate-700/20 max-w-full">
            <table className="min-w-full text-left border-collapse text-[11px] font-sans">
              <thead>
                <tr className={`${isLightTheme ? "bg-slate-100 text-slate-800" : "bg-black/40 text-[#F6A400]"} border-b border-slate-700/10`}>
                  {headerRow.map((cell, idx) => (
                    <th key={idx} className="p-2.5 font-bold uppercase tracking-wider">
                      {processTextInline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/10">
                {bodyRows.map((row, rIdx) => (
                  <tr key={rIdx} className={isLightTheme ? "hover:bg-slate-50 text-slate-700" : "hover:bg-white/5 text-slate-300"}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="p-2.5 leading-normal">
                        {processTextInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      currentTableLines = [];
    }
  };

  const flushAll = () => {
    flushList();
    flushTable();
  };

  const processTextInline = (sourceText: string) => {
    if (!sourceText) return "";
    
    // Split by ** to support bold highlights
    const parts = sourceText.split(/\*\*([^*]+)\*\*/g);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <strong 
            key={index} 
            className={`font-black tracking-tight ${isLightTheme ? "text-slate-900 border-b border-amber-300" : "text-white border-b border-[#d4af37]/35"}`}
          >
            {part}
          </strong>
        );
      }
      
      // Handle inline code `code`
      const codeParts = part.split(/`([^`]+)`/g);
      return codeParts.map((subPart, subIndex) => {
        if (subIndex % 2 === 1) {
          const copyValue = String(subPart || "").trim();
          const isCopied = copiedInlineCode === copyValue;
          return (
            <button
              key={subIndex}
              type="button"
              onClick={(event) => handleCopyInlineCode(event, copyValue)}
              className={`group/inlinecopy inline-flex max-w-full items-start gap-1.5 rounded-md border px-2 py-1 align-top font-mono text-[11px] font-black mx-1 my-0.5 text-left leading-4 whitespace-normal break-words transition-all duration-100 cursor-pointer ${
                isLightTheme
                  ? "bg-slate-100 text-amber-800 border-slate-200 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-900"
                  : "bg-black/40 text-amber-300 border-white/5 hover:bg-[#F6A400]/12 hover:border-[#F6A400]/35 hover:text-[#FFB72B]"
              }`}
              style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              title="Copy this invoice description / inline value"
              aria-label={`Copy ${copyValue}`}
            >
              <span className="min-w-0 whitespace-normal break-words text-left leading-4" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>{subPart}</span>
              {isCopied ? (
                <Check className="h-3 w-3 shrink-0 text-emerald-500" />
              ) : (
                <Copy className={`h-3 w-3 shrink-0 opacity-65 transition-opacity group-hover/inlinecopy:opacity-100 ${
                  isLightTheme ? "text-amber-700" : "text-[#FFB72B]"
                }`} />
              )}
            </button>
          );
        }
        return (
          <span key={subIndex}>
            <LinkValidatedContent text={subPart} />
          </span>
        );
      });
    });
  };

  let isInCodeBlock = false;
  let codeLines: string[] = [];
  let codeLanguage = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Do not render standalone shell labels like "CMD" or "PowerShell" when
    // they are immediately followed by a fenced code block. The code card
    // already displays the shell label, so rendering both looks duplicated.
    const nextNonEmptyLine = lines.slice(i + 1).find((candidate) => candidate.trim() !== "");
    const nextStartsCodeBlock = Boolean(nextNonEmptyLine && nextNonEmptyLine.trim().startsWith("```"));
    const isStandaloneShellLabel = /^(cmd(?:\s+admin)?|command prompt(?:\s+admin)?|powershell(?:\s+admin)?|power shell(?:\s+admin)?|ps1|pwsh|bash|shell|registry|reg|run dialog)$/i.test(trimmed);
    if (!isInCodeBlock && isStandaloneShellLabel && nextStartsCodeBlock) {
      continue;
    }

    if (trimmed.startsWith("```")) {
      flushList();
      flushTable();
      if (!isInCodeBlock) {
        isInCodeBlock = true;
        codeLanguage = trimmed.substring(3).trim().toLowerCase();
        codeLines = [];
      } else {
        isInCodeBlock = false;
        const codeText = codeLines.join("\n");
        parsedElements.push(
          <RenderedCodeBlock 
            key={`code-block-${i}`} 
            code={codeText} 
            language={codeLanguage} 
            isLightTheme={isLightTheme} 
          />
        );
      }
      continue;
    }

    if (isInCodeBlock) {
      codeLines.push(line);
      continue;
    }

    // Check for Markdown Table Rows
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      flushList();
      currentTableLines.push(trimmed);
    } else if (trimmed.startsWith("|") && trimmed.includes("|") && line.replace(/\s/g, "").startsWith("|")) {
      // Loose table row match
      flushList();
      currentTableLines.push(trimmed);
    } else {
      flushTable();

      // 1. Markdown Headings
      if (trimmed.startsWith("##### ")) {
        flushList();
        const content = trimmed.substring(6);
        parsedElements.push(
          <h6 
            key={i} 
            className={`text-[10px] font-mono font-black tracking-wider uppercase mt-4 mb-2 flex items-center gap-1.5 ${
              isLightTheme ? "text-slate-500" : "text-slate-400"
            }`}
          >
            ● {processTextInline(content)}
          </h6>
        );
      } else if (trimmed.startsWith("#### ")) {
        flushList();
        const content = trimmed.substring(5);
        parsedElements.push(
          <h5 
            key={i} 
            className={`text-xs font-mono font-black tracking-wider uppercase mt-4 mb-2 flex items-center gap-2 ${
              isLightTheme ? "text-amber-800" : "text-yellow-600"
            }`}
          >
            <span className="text-[9px] opacity-40">■</span> {processTextInline(content)}
          </h5>
        );
      } else if (trimmed.startsWith("### ")) {
        flushList();
        const content = trimmed.substring(4);
        parsedElements.push(
          <h4 
            key={i} 
            className={`text-xs font-mono font-black tracking-widest uppercase mt-5 mb-2.5 flex items-center gap-2 border-b pb-1.5 ${
              isLightTheme ? "text-amber-700 border-slate-200" : "text-yellow-500 border-white/5"
            }`}
          >
            <span className="text-[10px] opacity-60">▶</span> {processTextInline(content)}
          </h4>
        );
      } else if (trimmed.startsWith("## ")) {
        flushList();
        const content = trimmed.substring(3);
        parsedElements.push(
          <h3 
            key={i} 
            className={`text-[13px] font-display font-black tracking-wider mt-6 mb-3 border-l-4 pl-3.5 ${
              isLightTheme ? "text-slate-900 border-amber-500" : "text-white border-[#d4af37]"
            }`}
          >
            {processTextInline(content)}
          </h3>
        );
      } else if (trimmed.startsWith("# ")) {
        flushList();
        const content = trimmed.substring(2);
        parsedElements.push(
          <h2 
            key={i} 
            className={`text-sm font-display font-black tracking-normal mt-7 mb-4 border-b pb-2 ${
              isLightTheme ? "text-slate-900 border-amber-500/50" : "text-white border-[#d4af37]/45"
            }`}
          >
            {processTextInline(content)}
          </h2>
        );
      }
      // 2. Main List bullets with check/gold formatting
      else if (trimmed.startsWith("* ") || trimmed.startsWith("- ") || trimmed.match(/^\d+\.\s/)) {
        let content = "";
        let bulletElement: React.ReactNode = null;

        if (trimmed.startsWith("* ")) {
          content = trimmed.substring(2);
          bulletElement = (
            <span className={`w-1.5 h-1.5 rounded-full inline-block shrink-0 mt-2 ${isLightTheme ? "bg-amber-600" : "bg-[#d4af37]"}`} />
          );
        } else if (trimmed.startsWith("- ")) {
          content = trimmed.substring(2);
          bulletElement = (
            <span className={`w-1.5 h-1.5 rounded-full inline-block shrink-0 mt-2 ${isLightTheme ? "bg-amber-600" : "bg-[#d4af37]"}`} />
          );
        } else {
          const numMatch = trimmed.match(/^(\d+)\.\s/);
          if (numMatch) {
            content = trimmed.substring(numMatch[0].length);
            bulletElement = (
              <span className={`text-[10.5px] font-mono font-black shrink-0 min-w-[14px] ${isLightTheme ? "text-amber-700" : "text-[#d4af37]"}`}>
                {numMatch[1]}.
              </span>
            );
          }
        }

        currentListItems.push(
          <li key={`li-${i}`} className="flex items-start gap-2 text-xs py-0.5 leading-relaxed">
            {bulletElement}
            <div className="flex-1 text-inherit">{processTextInline(content)}</div>
          </li>
        );
      }
      // 3. Simple divider or empty spaces
      else if (trimmed === "") {
        flushList();
        if (parsedElements.length > 0) {
          parsedElements.push(<div key={`space-${i}`} className="h-2.5" />);
        }
      }
      // 4. Default plain paragraph line
      else {
        flushList();
        parsedElements.push(
          <p key={i} className="text-xs leading-relaxed text-inherit mb-1.5 font-medium">
            {processTextInline(line)}
          </p>
        );
      }
    }
  }

  flushAll();

  return <div className="space-y-1 text-left w-full">{parsedElements}</div>;
}

export default function ChatWorkspace({
  messages,
  currentMode,
  onModeChange,
  activeContextCount,
  totalContextCharacters,
  driveScopeCount,
  loadedContextCount,
  manualContextCount,
  pinnedContextCount,
  treeItemCount,
  loading,
  requestProgressSteps = [],
  driveSyncEnabled = true,
  driveSidebarVisible = true,
  onToggleDriveSidebar,
  onOpenExporter,
  onCreateNotes,
  savedChatFolders = [],
  savedChats = [],
  activeSavedChatId = null,
  defaultSavedChatFolderId,
  suggestedSavedChatTitle,
  onSaveCurrentChatToFolder,
  onCreateSavedChatFolder,
  globalTheme,
  onChangeGlobalTheme,
  chatTheme,
  onChangeChatTheme,
  inputTheme = "rodgerspc",
  onChangeInputTheme,
  inputPanelVisible = true,
  onToggleInputPanel,
  onClearChat,
  onUpdateMessage,
  onUpdateReport,
  dbLoaded = true,
  hasUnclearedChat = false,
  onContinueLastChat,
  onClearLastChatBackup,
  lastChatBackupClean = false,
  lastSavedChatCount = 0,
  onRetryMessage,
  onCancelRequest
}: ChatWorkspaceProps) {
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [copiedImgId, setCopiedImgId] = useState<string | null>(null);
  const chatStreamRef = useRef<HTMLDivElement>(null);
  const [showScrollBottomBtn, setShowScrollBottomBtn] = useState(false);

  // Custom Resize state fields
  const [activeCustomResizeMsgId, setActiveCustomResizeMsgId] = useState<string | null>(null);
  const [customWidth, setCustomWidth] = useState<number>(1348);
  const [customHeight, setCustomHeight] = useState<number>(1020);
  const [customUnit, setCustomUnit] = useState<"pixels" | "inches">("pixels");
  const [customFit, setCustomFit] = useState<"contain" | "cover" | "stretch">("contain");
  const [customBg, setCustomBg] = useState<"transparent" | "white" | "black" | "blur">("blur");

  // Inline edit state fields
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>("");
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [editingReportText, setEditingReportText] = useState<string>("");

  const downloadResizedCustom = (
    base64DataUrl: string, 
    customWidthVal: number, 
    customHeightVal: number, 
    unitVal: "pixels" | "inches",
    fitVal: "contain" | "cover" | "stretch",
    bgVal: "transparent" | "white" | "black" | "blur"
  ) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ppi = 96; // Standard screen ppi
      let targetW = customWidthVal;
      let targetH = customHeightVal;
      if (unitVal === "inches") {
        targetW = Math.round(customWidthVal * ppi);
        targetH = Math.round(customHeightVal * ppi);
      }

      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      const origW = img.naturalWidth || img.width || 512;
      const origH = img.naturalHeight || img.height || 512;
      const origRatio = origW / origH;
      const targetRatio = targetW / targetH;

      // 1. Render background preset
      if (bgVal === "white") {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, targetW, targetH);
      } else if (bgVal === "black") {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, targetW, targetH);
      } else if (bgVal === "blur") {
        // Draw design stretched ambient backing block
        ctx.drawImage(img, 0, 0, targetW, targetH);
        ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
        ctx.fillRect(0, 0, targetW, targetH);
      } else {
        ctx.clearRect(0, 0, targetW, targetH);
      }

      // 2. Fits & margins mapping
      let drawW = targetW;
      let drawH = targetH;
      let drawX = 0;
      let drawY = 0;

      if (fitVal === "stretch") {
        ctx.drawImage(img, 0, 0, targetW, targetH);
      } else if (fitVal === "cover") {
        if (origRatio > targetRatio) {
          drawH = targetH;
          drawW = Math.round(targetH * origRatio);
          drawX = Math.round((targetW - drawW) / 2);
        } else {
          drawW = targetW;
          drawH = Math.round(targetW / origRatio);
          drawY = Math.round((targetH - drawH) / 2);
        }
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
      } else {
        // 'contain' fit mode
        if (origRatio > targetRatio) {
          drawW = targetW;
          drawH = Math.round(targetW / origRatio);
          drawY = Math.round((targetH - drawH) / 2);
        } else {
          drawH = targetH;
          drawW = Math.round(targetH * origRatio);
          drawX = Math.round((targetW - drawW) / 2);
        }
        ctx.drawImage(img, drawX, drawY, drawW, drawH);
      }

      // 3. Save as formal file download callback
      try {
        const format = (base64DataUrl.includes("image/png") || bgVal === "transparent") ? "image/png" : "image/jpeg";
        const resizedUrl = canvas.toDataURL(format);
        const link = document.createElement("a");
        link.href = resizedUrl;
        link.download = `RodgersPC_Resize_${targetW}x${targetH}.${format === "image/png" ? "png" : "jpg"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error("Custom scale downscale failure context:", e);
      }
    };
    img.src = base64DataUrl;
  };

  const handleScroll = () => {
    const el = chatStreamRef.current;
    if (!el) return;
    // Show button if scrolled up from the bottom by 180px
    const isScrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > 180;
    setShowScrollBottomBtn(isScrolledUp);
  };

  const scrollToBottom = () => {
    const el = chatStreamRef.current;
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  // Auto-scroll to bottom on loading or on new messages
  useEffect(() => {
    const el = chatStreamRef.current;
    if (el) {
      const timer = setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages.length, loading]);

  const handleCopyText = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadFile = (filename: string, text: string, type: "pdf" | "docx" | "txt") => {
    const element = document.createElement("a");
    const headerPrefix = `[RODGERSPC MESSAGE EXPORT: ${type.toUpperCase()}]\n`;
    const finalContent = headerPrefix + text;
    const file = new Blob([finalContent], { type: "text/plain;charset=utf-8" });
    element.href = URL.createObjectURL(file);
    element.download = `${filename.replace(/\s+/g, "_")}.${type}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const copyImageToClipboard = async (base64DataUrl: string) => {
    try {
      const res = await fetch(base64DataUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      return true;
    } catch (err) {
      console.error("Failed to copy image to clipboard:", err);
      try {
        await navigator.clipboard.writeText(base64DataUrl);
        return true;
      } catch {
        return false;
      }
    }
  };

  const downloadResizedImage = (base64DataUrl: string, targetWidthPercentOrPx: number | "original") => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      let width = img.naturalWidth || img.width || 512;
      let height = img.naturalHeight || img.height || 512;
      const aspectRatio = width / height;

      if (targetWidthPercentOrPx !== "original") {
        if (targetWidthPercentOrPx <= 1) {
          width = Math.round(width * targetWidthPercentOrPx);
        } else {
          width = targetWidthPercentOrPx;
        }
        height = Math.round(width / aspectRatio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);
        try {
          const resizedUrl = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = resizedUrl;
          link.download = `RodgersPC_Visual_${width}x${height}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (e) {
          const link = document.createElement("a");
          link.href = base64DataUrl;
          link.download = `RodgersPC_Visual_Original.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    };
    img.src = base64DataUrl;
  };

  // Mode list: labels stay visible; descriptions are kept as hover/help text only.
  // Operational behavior is enforced in server.ts. The chat UI must stay clean.
  const modesList = [
    {
      id: AppMode.TECH_ASSISTANT,
      label: "Bench Tech",
      icon: Wrench,
      desc: "Live bench diagnostics, evidence control, safe commands, repair decisions, validation, and documentation. Must not guess."
    },
    {
      id: AppMode.BLOG_ENGINE,
      label: "Blog Engine",
      icon: BookOpen,
      desc: "RodgersPC blog outlines, FAQs, service-page copy, internal links, and local educational content."
    },
    {
      id: AppMode.PERSONAL_VAULT,
      label: "Personal Vault",
      icon: Lock,
      desc: "Joshua's private planning, internal notes, decision records, and memory-style organization. Not public copy."
    },
    {
      id: AppMode.INVENTORY_SCOUT,
      label: "Inventory Scout",
      icon: Package,
      desc: "Parts sourcing with exact-match discipline, seller/condition, warranty, stock, delivery, price confidence, and links."
    },
    {
      id: AppMode.LEAD_RESPONDER,
      label: "Lead Responder",
      icon: MessageSquare,
      desc: "Copy-ready texts, emails, leads, and reviews. Customer-safe, plain English, no overpromising."
    },
    {
      id: AppMode.SEO_LISTINGS,
      label: "SEO & Listings",
      icon: TrendingUp,
      desc: "Google Business Profile, local SEO, listings, schema, service pages, and Central Texas wording."
    },
    {
      id: AppMode.WEB_CREATIVE,
      label: "Web Creative",
      icon: Palette,
      desc: "Website sections, landing pages, branded creative, counter cards, ad copy, and design prompts."
    },
    {
      id: AppMode.FRONT_DESK,
      label: "Front Desk",
      icon: Briefcase,
      desc: "Counter wording, intake, pricing explanations, invoices, customer updates, policies, and shop workflow."
    },
  ];

  const getModeLabel = (modeId: AppMode): string => {
    const found = modesList.find(m => m.id === modeId);
    return found ? found.label : modeId;
  };

  const getEmptyModeCardCopy = (modeId: AppMode): string => {
    switch (modeId) {
      case AppMode.TECH_ASSISTANT:
        return "Live bench diagnostics, safe commands, validation.";
      case AppMode.BLOG_ENGINE:
        return "Local articles, FAQs, service pages, internal links.";
      case AppMode.PERSONAL_VAULT:
        return "Private plans, decisions, continuity, memory.";
      case AppMode.INVENTORY_SCOUT:
        return "Exact-match sourcing, seller checks, delivery risk.";
      case AppMode.LEAD_RESPONDER:
        return "Customer replies, reviews, leads, follow-ups.";
      case AppMode.SEO_LISTINGS:
        return "GBP, listings, schema, local SEO copy.";
      case AppMode.WEB_CREATIVE:
        return "Website sections, ads, counter cards, prompts.";
      case AppMode.FRONT_DESK:
        return "Intake, pricing wording, policies, updates.";
      default:
        return "RodgersPC workspace mode.";
    }
  };

  const [requestElapsedSeconds, setRequestElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!loading) {
      setRequestElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setRequestElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [loading]);

  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const [copiedDiagnosticLog, setCopiedDiagnosticLog] = useState(false);

  const visibleProgressSteps = requestProgressSteps.length
    ? requestProgressSteps
    : ["Queued message.", "Preparing request packet.", "Waiting for live server trace."];

  const currentProgressStep = visibleProgressSteps[visibleProgressSteps.length - 1] || "Working...";

  const diagnosticLogText = React.useMemo(() => {
    const lines = [
      "RODGERSPC COMMAND CENTER DIAGNOSTIC TRACE",
      `Mode: ${formatModeLabel(currentMode)}`,
      `Loading: ${loading ? "true" : "false"}`,
      `Elapsed: ${requestElapsedSeconds}s`,
      `Progress events: ${visibleProgressSteps.length}`,
      "",
      "EVENTS:",
      ...visibleProgressSteps.map((step, index) => {
        const cleaned = String(step || "")
          .replace(/^[-•>\s]+/, "")
          .replace(/^Server:\s*/i, "srv ▸ ")
          .replace(/^Client:\s*/i, "ui ▸ ")
          .trim();

        return `${String(index + 1).padStart(2, "0")}. ${cleaned}`;
      }),
    ];

    return lines.join("\n");
  }, [currentMode, loading, requestElapsedSeconds, visibleProgressSteps]);

  const handleCopyDiagnosticLog = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticLogText);
      setCopiedDiagnosticLog(true);
      window.setTimeout(() => setCopiedDiagnosticLog(false), 1800);
    } catch (err) {
      console.error("Failed to copy diagnostic trace:", err);
    }
  };

  const isGlobalLight = globalTheme === "light";
  const isGlobalDark = globalTheme === "dark";
  const isGlobalRodgers = globalTheme === "rodgerspc";

  // Global styling classes for header/modes
  const headerBgClassName = isGlobalLight
    ? "bg-white/72 backdrop-blur-xl border-b border-white/70 text-[#122033] shadow-[0_8px_30px_rgba(15,23,42,0.08)]"
    : isGlobalDark
      ? "bg-[#10121A]/88 backdrop-blur-xl border-b border-white/10 text-[#F4F6FA]"
      : "bg-[#0B2F73]/88 backdrop-blur-xl border-b border-[#F6A400]/25 text-white";

  const mainBgClassName = isGlobalLight
    ? "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,1),rgba(230,236,246,0.96)_42%,rgba(213,224,240,0.94))] text-[#122033]"
    : isGlobalDark
      ? "bg-[#080A0F] text-[#F4F6FA]"
      : "bg-[#06152E] text-white";

  const borderColClassName = isGlobalLight
    ? "border-white/70"
    : isGlobalDark
      ? "border-white/10"
      : "border-[#F6A400]/25";

  const textGoldClassName = isGlobalLight
    ? "text-[#143F8F]"
    : isGlobalDark
      ? "text-[#F6A400]"
      : "text-[#FFB72B]";

  const bgBadgeClassName = isGlobalLight
    ? "bg-white/70 border-white/80 text-[#143F8F]"
    : isGlobalDark
      ? "bg-[#F6A400]/10 border-[#F6A400]/20 text-[#F6A400]"
      : "bg-[#F6A400]/20 border-[#F6A400]/40 text-[#FFB72B]";

  const textTitleClassName = isGlobalLight
    ? "text-[#122033]"
    : isGlobalDark
      ? "text-[#F4F6FA]"
      : "text-white";

  // Chat stream-specific styling classes. Chat theme is independent from app shell and input panel.
  const isChatLight = chatTheme === "light";
  const isChatDark = chatTheme === "dark";
  const isChatRodgers = chatTheme === "rodgerspc";

  const chatStreamBgClassName = isChatLight
    ? "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.96),transparent_26rem),linear-gradient(145deg,rgba(250,252,255,0.94),rgba(232,238,248,0.84))] text-[#122033]"
    : isChatDark
      ? "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.045),transparent_22rem),radial-gradient(circle_at_bottom_right,rgba(126,166,232,0.08),transparent_26rem),linear-gradient(145deg,#0C0F16,#151A24_52%,#090B10)] text-[#F5F7FB]"
      : "bg-[radial-gradient(circle_at_top_left,rgba(246,164,0,0.11),transparent_24rem),radial-gradient(circle_at_bottom_right,rgba(47,111,228,0.18),transparent_30rem),linear-gradient(145deg,#06152E,#0A2452_54%,#071226)] text-white";

  const chatBorderClassName = isChatLight
    ? "border-white/70"
    : isChatDark
      ? "border-white/10"
      : "border-[#F6A400]/25";

  const userBubbleStyle = isChatLight
    ? "bg-white/82 backdrop-blur-xl border-white/80 text-slate-900 shadow-[0_12px_34px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.86)]"
    : isChatDark
      ? "bg-[linear-gradient(180deg,rgba(38,44,58,0.88),rgba(21,25,35,0.88))] border-white/10 text-slate-100 shadow-[0_14px_36px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,0.06)]"
      : "bg-[linear-gradient(180deg,rgba(23,69,143,0.82),rgba(13,42,93,0.86))] border-[#F6A400]/14 text-white shadow-[0_12px_34px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.08)]";

  const assistantBubbleStyle = isChatLight
    ? "bg-white/68 backdrop-blur-xl border-white/74 text-[#122033] shadow-[0_10px_30px_rgba(15,23,42,0.06),inset_0_1px_0_rgba(255,255,255,0.78)]"
    : isChatDark
      ? "bg-[linear-gradient(180deg,rgba(24,28,38,0.86),rgba(13,16,23,0.90))] border-white/[0.08] text-slate-100 shadow-[0_12px_34px_rgba(0,0,0,0.26),inset_0_1px_0_rgba(255,255,255,0.045)]"
      : "bg-[linear-gradient(180deg,rgba(11,35,72,0.78),rgba(7,20,47,0.84))] border-[#F6A400]/14 text-slate-100 shadow-[0_12px_34px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,0.055)]";

  const getModeCardShellClass = (
    theme: UiTheme,
    isActive: boolean,
    surface: "dropdown" | "empty",
  ): string => {
    const base = surface === "dropdown"
      ? "font-sans"
      : "shadow-[inset_0_1px_0_rgba(255,255,255,0.055)]";

    if (theme === "light") {
      return isActive
        ? `bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(235,241,250,0.88))] text-[#143F8F] border-[#143F8F]/42 shadow-[0_16px_38px_rgba(20,63,143,0.12),inset_0_1px_0_rgba(255,255,255,0.95)] font-bold ${base}`
        : `bg-[linear-gradient(180deg,rgba(255,255,255,0.72),rgba(236,241,248,0.62))] hover:bg-white/92 border-white/76 text-slate-700 hover:text-slate-950 shadow-[0_10px_26px_rgba(15,23,42,0.06),inset_0_1px_0_rgba(255,255,255,0.82)] ${base}`;
    }

    if (theme === "dark") {
      return isActive
        ? `bg-[linear-gradient(180deg,rgba(45,51,65,0.92),rgba(23,27,37,0.96))] text-slate-100 border-slate-400/34 shadow-[0_18px_42px_rgba(0,0,0,0.36),0_0_0_1px_rgba(255,255,255,0.04),inset_0_1px_0_rgba(255,255,255,0.10)] font-bold ${base}`
        : `bg-[linear-gradient(180deg,rgba(34,39,51,0.74),rgba(18,22,31,0.82))] hover:bg-white/[0.09] border-white/10 text-slate-300 hover:text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${base}`;
    }

    return isActive
      ? `bg-[linear-gradient(145deg,rgba(17,58,134,0.96),rgba(6,26,59,0.98))] text-[#FFB72B] border-[#F6A400]/72 shadow-[0_18px_44px_rgba(246,164,0,0.16),0_0_0_1px_rgba(246,164,0,0.10),inset_0_1px_0_rgba(255,255,255,0.11)] font-bold ${base}`
      : `bg-[linear-gradient(180deg,rgba(13,46,102,0.66),rgba(8,28,62,0.72))] hover:bg-[#123B80]/82 border-[#F6A400]/14 text-slate-200 hover:text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.045)] ${base}`;
  };

  const getModeIconShellClass = (theme: UiTheme, isActive: boolean): string => {
    if (theme === "light") {
      return isActive
        ? "bg-[#143F8F]/8 border-[#143F8F]/20 text-[#143F8F]"
        : "bg-white/70 border-slate-200/90 text-[#143F8F]";
    }

    if (theme === "dark") {
      return isActive
        ? "bg-white/[0.075] border-slate-300/24 text-slate-100"
        : "bg-black/20 border-white/10 text-slate-300";
    }

    return isActive
      ? "bg-[#F6A400]/13 border-[#F6A400]/38 text-[#FFB72B]"
      : "bg-black/20 border-white/10 text-slate-300";
  };

  const getModeDescriptionClass = (theme: UiTheme): string => {
    if (theme === "light") return "text-slate-600";
    if (theme === "dark") return "text-slate-400";
    return "text-slate-300";
  };

  const formatLocalDateForTitle = (): string => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const normalizeTitleSearchText = (value: unknown): string => {
    return String(value || "")
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const buildSmartSuggestedSaveChatTitle = (): string => {
    const fallback = String(suggestedSavedChatTitle || "").trim();
    const date = formatLocalDateForTitle();

    const userText = messages
      .filter((message) => message.role === "user")
      .map((message) => String(message.content || ""))
      .join("\n\n");

    const normalized = normalizeTitleSearchText(userText);
    const isLeadResponder = currentMode === AppMode.LEAD_RESPONDER || currentMode === AppMode.FRONT_DESK;

    if (!isLeadResponder || !normalized) {
      return fallback || `${formatModeLabel(currentMode)} Chat`;
    }

    const hasNextdoor =
      normalized.includes("nextdoor") ||
      normalized.includes("neighbor") ||
      normalized.includes("recommendation") ||
      normalized.includes("recommendations") ||
      normalized.includes("shoutout") ||
      normalized.includes("shout out");

    const hasGustavo = normalized.includes("gustavo");
    const hasGamingDisplayInquiry =
      normalized.includes("gaming") ||
      normalized.includes("screen goes black") ||
      normalized.includes("black screen") ||
      normalized.includes("locks up") ||
      normalized.includes("tv") ||
      normalized.includes("external display");

    if (hasNextdoor && hasGustavo && hasGamingDisplayInquiry) {
      return `Lead Responder – Nextdoor Shoutout + Gustavo Gaming Inquiry – ${date}`;
    }

    const likelyLeadMarkers = [
      "nextdoor",
      "facebook",
      "google review",
      "review",
      "email",
      "reply",
      "respond",
      "message",
      "customer",
      "lead",
      "inquiry",
      "quote",
      "diagnostic",
      "bring it by",
      "no appointment",
    ];

    const markerHits = likelyLeadMarkers.filter((marker) => normalized.includes(marker)).length;
    const namedPersonHits = Array.from(new Set((userText.match(/\b[A-Z][a-z]{2,}\b/g) || [])
      .filter((word) => !["RodgersPC", "Customer", "Nextdoor", "Google", "Facebook", "Belton", "Temple", "Killeen"].includes(word))));

    const hasMixedLeadSignals =
      (hasNextdoor && markerHits >= 2) ||
      namedPersonHits.length >= 2 ||
      (normalized.includes("first") && normalized.includes("second")) ||
      (normalized.includes("another") && (normalized.includes("reply") || normalized.includes("lead")));

    if (hasMixedLeadSignals) {
      return `Lead Responder – Mixed Leads – ${date}`;
    }

    return fallback || `${formatModeLabel(currentMode)} Chat`;
  };

  const smartSuggestedSaveChatTitle = buildSmartSuggestedSaveChatTitle();

  const [isConsoleDropdownOpen, setIsConsoleDropdownOpen] = React.useState(false);
  const [isSaveChatOpen, setIsSaveChatOpen] = useState(false);
  const [saveChatFolderId, setSaveChatFolderId] = useState<string>(defaultSavedChatFolderId || "");
  const [saveChatTitle, setSaveChatTitle] = useState<string>(smartSuggestedSaveChatTitle || "");
  const [newFolderName, setNewFolderName] = useState<string>("");

  useEffect(() => {
    if (!isSaveChatOpen) return;
    setSaveChatFolderId(defaultSavedChatFolderId || savedChatFolders[0]?.id || "");
    setSaveChatTitle(smartSuggestedSaveChatTitle || "");
    setNewFolderName("");
  }, [isSaveChatOpen, defaultSavedChatFolderId, smartSuggestedSaveChatTitle, savedChatFolders]);

  const handleCreateFolderFromSavePopover = () => {
    const name = newFolderName.trim();
    if (!name || !onCreateSavedChatFolder) return;
    const created = onCreateSavedChatFolder(name);
    if (created?.id) {
      setSaveChatFolderId(created.id);
    }
    setNewFolderName("");
  };

  const handleSaveChatFromPopover = () => {
    if (!onSaveCurrentChatToFolder || messages.length === 0) return;
    const finalFolderId = saveChatFolderId || defaultSavedChatFolderId || savedChatFolders[0]?.id || "";
    if (!finalFolderId) return;
    onSaveCurrentChatToFolder(finalFolderId, saveChatTitle);
    setIsSaveChatOpen(false);
  };

  const activeSavedChatTitle = activeSavedChatId
    ? savedChats.find((chat) => chat.id === activeSavedChatId)?.title
    : "";

  const isRetryableAssistantFailure = (msg: ChatMessage): boolean => {
    if (msg.role !== "assistant") return false;
    const text = (msg.content || "").toLowerCase();
    return (
      text.includes("request failed") ||
      text.includes("temporarily unavailable") ||
      text.includes("overloaded") ||
      text.includes("connectivity disruption") ||
      text.includes("system submission timeout") ||
      text.includes("system server disruption") ||
      text.includes("failed to dispatch")
    );
  };


  const renderThemeControl = (
    label: string,
    value: UiTheme,
    onChange?: (theme: UiTheme) => void,
  ) => {
    const choices: { value: UiTheme; label: string; icon: React.ReactNode }[] = [
      { value: "rodgerspc", label: "RPC", icon: <Terminal className="h-2.5 w-2.5" /> },
      { value: "dark", label: "Dark", icon: <Moon className="h-2.5 w-2.5" /> },
      { value: "light", label: "Light", icon: <Sun className="h-2.5 w-2.5" /> },
    ];

    const shellClass = isGlobalLight
      ? "bg-white/72 border-white/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_8px_18px_rgba(15,23,42,0.055)]"
      : "bg-black/18 border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.045)]";

    return (
      <div className={`rpc-theme-segment flex items-center gap-1 rounded-lg border p-0.5 ${shellClass}`}>
        <span className={`px-1.5 text-[7.5px] font-black uppercase tracking-[0.10em] ${
          isGlobalLight ? "text-slate-500" : "text-slate-400"
        }`}>
          {label}
        </span>
        {choices.map((choice) => {
          const active = value === choice.value;
          const activeClass =
            choice.value === "light"
              ? "bg-white text-slate-950 border-slate-300 shadow-sm"
              : choice.value === "dark"
                ? "bg-[#1A202C] text-white border-white/18 shadow-sm"
                : "bg-[#F6A400]/14 text-[#FFB72B] border-[#F6A400]/45 shadow-sm";
          const idleClass = isGlobalLight
            ? "text-slate-500 hover:bg-white/85 hover:text-slate-950 border-transparent"
            : "text-slate-400 hover:bg-white/[0.07] hover:text-white border-transparent";

          return (
            <button
              key={`${label}-${choice.value}`}
              type="button"
              onClick={() => onChange?.(choice.value)}
              disabled={!onChange}
              className={`inline-flex h-5 items-center gap-1 rounded-md border px-1.5 text-[7.5px] font-black uppercase tracking-[0.035em] transition-all duration-100 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
                active ? activeClass : idleClass
              }`}
              title={`${label} ${choice.label}`}
            >
              {choice.icon}
              <span>{choice.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full flex-1 ${mainBgClassName} relative`}>
      {/* PHASE 6: compact premium top bar. Operational toggles live in InputEngine; Drive scope lives in DriveWorkspace. */}
      <div className={`h-12 px-4 shrink-0 ${headerBgClassName} flex items-center justify-between relative z-40`}>
        <div className="flex items-center gap-2">
          {/* Glowing Status Matrix Node */}
          <div className={`w-2.5 h-2.5 rounded-full animate-pulse mr-1 border ${
            isGlobalLight 
              ? "bg-amber-500 border-amber-300" 
              : isGlobalDark 
                ? "bg-teal-400 border-teal-300" 
                : "bg-[#d4af37] border-[#ffdf7a]"
          }`} />
          <h1 className={`text-[11px] font-black tracking-[0.18em] uppercase font-mono ${textTitleClassName} select-none hidden md:inline-block`}>
            Command Center
          </h1>

          {/* Golden/Teal 3D Selected Mode Pill. Clicking toggles full dropdown center! */}
          <button 
            type="button"
            onClick={() => setIsConsoleDropdownOpen(!isConsoleDropdownOpen)}
            className={`rpc-toolbar-btn h-8 px-3 rounded-xl text-[10px] uppercase font-bold tracking-wider cursor-pointer border shadow-sm ${
              isGlobalLight
                ? "bg-white/72 backdrop-blur-xl hover:bg-white text-[#143F8F] border-white/80"
                : isGlobalDark
                  ? "bg-white/[0.06] hover:bg-white/[0.10] text-[#F6A400] border-white/10"
                  : "bg-gradient-to-b from-[#122444]/95 to-[#08152e]/95 hover:from-[#19325f] hover:to-[#0c1c38] text-[#FFB72B] border-[#F6A400]/35"
            }`}
            title="Switch RodgersPC business mode"
            id="toggle-console-dropdown-btn"
          >
            <Terminal className={`w-3.5 h-3.5 ${textGoldClassName}`} />
            <span>{getModeLabel(currentMode).toUpperCase()}</span>
            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isConsoleDropdownOpen ? "rotate-180" : ""}`} />
          </button>

        </div>

        {/* Layout Control Triggers */}
        <div className="flex items-center gap-2">
          
          {/* Toggle Core Vault Left Panel */}
          {driveSyncEnabled && onToggleDriveSidebar && (
            <div className="relative group/tooltip flex items-center">
              <button
                onClick={onToggleDriveSidebar}
                className={`rpc-toolbar-btn w-7 rounded-lg border transition-all duration-150 cursor-pointer active:translate-y-[1px] ${
                  driveSidebarVisible
                    ? (isGlobalLight ? "bg-gradient-to-b from-amber-400 to-amber-550 text-white border-amber-500" : isGlobalDark ? "bg-teal-900/30 text-teal-400 border-teal-850" : "bg-gradient-to-b from-[#112240] to-[#020b1e] border-[#F6A400]/40 text-[#FFB72B]")
                    : (isGlobalLight ? "bg-white border-slate-300 text-slate-500 hover:text-slate-700" : isGlobalDark ? "bg-neutral-800 border-neutral-700 text-neutral-400 hover:text-neutral-200" : "bg-[#112240] border-white/10 text-slate-400 hover:text-white")
                }`}
                aria-label="Toggle Drive Brain Sidebar"
                id="topbar-toggle-vault-sidebar"
              >
                <FolderOpen className="w-3.5 h-3.5 shrink-0" />
              </button>
              <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded bg-[#10121A] border border-neutral-700/60 px-2 py-0.5 text-[8.5px] font-bold text-white opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 shadow-xl z-50">
                {driveSidebarVisible ? "Collapse Drive Brain" : "Show Drive Brain"}
              </span>
            </div>
          )}

          {/* Toggle Bottom Input Panel */}
          {onToggleInputPanel && (
            <div className="relative group/tooltip flex items-center">
              <button
                onClick={onToggleInputPanel}
                className={`rpc-toolbar-btn w-7 rounded-lg border transition-all duration-150 cursor-pointer active:translate-y-[1px] ${
                  inputPanelVisible
                    ? (isGlobalLight ? "bg-gradient-to-b from-amber-400 to-amber-550 text-white border-amber-500" : isGlobalDark ? "bg-teal-900/30 text-teal-400 border-teal-850" : "bg-gradient-to-b from-[#112240] to-[#020b1e] border-[#F6A400]/40 text-[#FFB72B]")
                    : (isGlobalLight ? "bg-white border-slate-300 text-slate-500 hover:text-slate-700" : isGlobalDark ? "bg-neutral-800 border-neutral-700 text-neutral-400 hover:text-neutral-200" : "bg-[#112240] border-white/10 text-slate-400 hover:text-white")
                }`}
                aria-label="Toggle Console Input Panel"
                id="topbar-toggle-bottom-input"
              >
                <Terminal className="w-3.5 h-3.5 shrink-0" />
              </button>
              <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded bg-[#10121A] border border-neutral-700/60 px-2 py-0.5 text-[8.5px] font-bold text-white opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 shadow-xl z-50">
                {inputPanelVisible ? "Collapse Input Panel" : "Restore Input Panel"}
              </span>
            </div>
          )}


          {/* Manual Save Chat memory button */}
          {onSaveCurrentChatToFolder && (
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setIsSaveChatOpen((prev) => !prev)}
                disabled={messages.length === 0}
                className={`rpc-toolbar-btn rpc-chat-save-btn rounded-lg border transition-all duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 ${
                  isGlobalLight
                    ? "bg-white/72 backdrop-blur-xl border-white/80 text-[#143F8F] hover:bg-white"
                    : isGlobalDark
                      ? "bg-white/[0.06] border-white/10 text-slate-200 hover:bg-white/[0.10]"
                      : "bg-[#112240] border-[#F6A400]/25 text-[#FFB72B] hover:bg-[#16366D]"
                }`}
                aria-label="Save current chat to Chats memory"
                id="topbar-save-chat-btn"
                title={activeSavedChatTitle ? `Save this chat as a new entry. Current open saved chat: ${activeSavedChatTitle}` : "Save this chat to Chats memory"}
              >
                <Save className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden lg:inline">Save Chat</span>
              </button>

              {isSaveChatOpen && (
                <>
                  <div
                    className="fixed inset-0 z-45 bg-transparent"
                    onClick={() => setIsSaveChatOpen(false)}
                  />
                  <div
                    className={`absolute right-0 top-full mt-2 z-50 w-[270px] rpc-save-chat-popover rounded-xl border p-2 shadow-2xl backdrop-blur-2xl ${
                      isGlobalLight
                        ? "border-white/80 bg-white/92 text-slate-900 shadow-[0_24px_70px_rgba(15,23,42,0.18)]"
                        : isGlobalDark
                          ? "border-white/10 bg-[#111827]/96 text-white shadow-black/80"
                          : "border-[#F6A400]/24 bg-[#081F49]/96 text-white shadow-black/80"
                    }`}
                    id="save-chat-popover"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div>
                        <div className={`text-[7.5px] font-black uppercase tracking-[0.13em] ${isGlobalLight ? "text-[#143F8F]" : "text-[#F6A400]"}`}>
                          Save to Chats Memory
                        </div>
                        <div className={`mt-0.5 text-[8px] leading-3 ${isGlobalLight ? "text-slate-500" : "text-white/50"}`}>
                          Durable cloud chat memory. Not Drive export.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsSaveChatOpen(false)}
                        className={`rounded-md p-1 transition ${isGlobalLight ? "text-slate-400 hover:bg-slate-100 hover:text-slate-900" : "text-white/45 hover:bg-white/10 hover:text-white"}`}
                        title="Close"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>

                    <label className={`mb-0.5 block text-[7.5px] font-black uppercase tracking-[0.09em] ${isGlobalLight ? "text-slate-500" : "text-white/42"}`}>
                      Title
                    </label>
                    <input
                      value={saveChatTitle}
                      onChange={(event) => setSaveChatTitle(event.target.value)}
                      className={`mb-1.5 h-5 w-full rounded-md border px-1.5 text-[8.5px] font-semibold outline-none ${
                        isGlobalLight
                          ? "border-slate-200 bg-slate-50 text-slate-900 focus:border-[#143F8F]/45"
                          : "border-white/10 bg-black/20 text-white focus:border-[#F6A400]/45"
                      }`}
                      placeholder="Chat title"
                    />

                    <div className={`mb-0.5 block text-[7.5px] font-black uppercase tracking-[0.09em] ${isGlobalLight ? "text-slate-500" : "text-white/42"}`}>
                      Folder
                    </div>
                    <div className="max-h-40 space-y-0.5 overflow-y-auto pr-1">
                      {savedChatFolders.map((folder) => {
                        const selected = folder.id === saveChatFolderId;
                        return (
                          <button
                            key={folder.id}
                            type="button"
                            onClick={() => setSaveChatFolderId(folder.id)}
                            className={`flex h-5 w-full items-center justify-between rounded-md border px-1.5 text-left text-[8.5px] font-semibold transition ${
                              selected
                                ? isGlobalLight
                                  ? "border-[#143F8F]/35 bg-[#143F8F]/8 text-[#143F8F]"
                                  : "border-[#F6A400]/35 bg-[#F6A400]/10 text-[#FFB72B]"
                                : isGlobalLight
                                  ? "border-slate-200 bg-white/70 text-slate-600 hover:text-slate-950"
                                  : "border-white/10 bg-white/[0.035] text-slate-300 hover:text-white"
                            }`}
                          >
                            <span className="truncate">{folder.name}</span>
                            {selected && <Check className="h-2.5 w-2.5 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-1.5 flex gap-1">
                      <input
                        value={newFolderName}
                        onChange={(event) => setNewFolderName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleCreateFolderFromSavePopover();
                          }
                        }}
                        className={`h-5 min-w-0 flex-1 rounded-md border px-1.5 text-[8.5px] outline-none ${
                          isGlobalLight
                            ? "border-slate-200 bg-slate-50 text-slate-900"
                            : "border-white/10 bg-black/20 text-white"
                        }`}
                        placeholder="New folder"
                      />
                      <button
                        type="button"
                        onClick={handleCreateFolderFromSavePopover}
                        disabled={!newFolderName.trim()}
                        className={`flex h-5 w-5 items-center justify-center rounded-[5px] border text-[8px] font-semibold leading-none opacity-55 transition hover:opacity-95 disabled:opacity-25 ${
                          isGlobalLight
                            ? "border-slate-200 bg-white text-slate-500 hover:text-[#143F8F]"
                            : "border-white/10 bg-white/[0.035] text-white/45 hover:text-[#F6A400]"
                        }`}
                        title="Create folder"
                        aria-label="Create folder"
                      >
                        +
                      </button>
                    </div>

                    <div className="mt-2 flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setIsSaveChatOpen(false)}
                        className={`h-5 rounded-md border px-1.5 text-[7.5px] font-black uppercase tracking-[0.035em] ${
                          isGlobalLight
                            ? "border-slate-200 bg-white text-slate-500 hover:text-slate-900"
                            : "border-white/10 bg-white/[0.035] text-white/50 hover:text-white"
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveChatFromPopover}
                        className={`h-5 rounded-md border px-2 text-[7.5px] font-black uppercase tracking-[0.035em] ${
                          isGlobalLight
                            ? "border-[#143F8F] bg-[#143F8F] text-white"
                            : "border-[#F6A400] bg-[#F6A400] text-[#071A3D]"
                        }`}
                      >
                        Save Chat
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Manual Notes button. Does not run until clicked. */}
          {onCreateNotes && (
            <div className="relative group/tooltip flex items-center">
              <button
                type="button"
                onClick={onCreateNotes}
                disabled={loading || messages.length === 0}
                className={`rpc-toolbar-btn rpc-notes-btn rounded-lg border transition-all duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 ${
                  isGlobalLight
                    ? "bg-white/72 backdrop-blur-xl border-white/80 text-[#143F8F] hover:bg-white"
                    : isGlobalDark
                      ? "bg-white/[0.06] border-white/10 text-slate-200 hover:bg-white/[0.10]"
                      : "bg-[#112240] border-[#F6A400]/25 text-[#FFB72B] hover:bg-[#16366D]"
                }`}
                aria-label="Create manual Notes"
                id="topbar-notes-btn"
                title="Create manual Notes from this chat"
              >
                <StickyNote className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden lg:inline">Notes</span>
              </button>
              <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded bg-[#10121A] border border-neutral-700/60 px-2 py-0.5 text-[8.5px] font-bold text-white opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 shadow-xl z-50">
                Manual Notes
              </span>
            </div>
          )}


          {/* AI Exporter button */}
          {onOpenExporter && (
            <div className="relative group/tooltip flex items-center">
              <button
                onClick={onOpenExporter}
                className={`rpc-toolbar-btn w-7 rounded-lg border transition-all duration-150 transform active:scale-95 cursor-pointer ${
                  isGlobalLight
                    ? "bg-white/72 backdrop-blur-xl border-white/80 text-emerald-700 hover:bg-white"
                    : "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/35 text-emerald-400"
                }`}
                aria-label="Open export panel"
                id="topbar-ai-exporter-btn"
              >
                <Download className="w-3.5 h-3.5 shrink-0 font-bold" />
              </button>
              <span className="pointer-events-none hidden group-hover/tooltip:block absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded-lg bg-[#10121A]/95 border border-neutral-700/70 px-2 py-0.5 text-[8px] font-bold text-white shadow-xl z-50">
                Export Chat
              </span>
            </div>
          )}

          {/* Clear Chat History button */}
          {onClearChat && (
            <div className="relative group/tooltip flex items-center">
              <button
                onClick={onClearChat}
                className={`rpc-toolbar-btn w-7 rounded-lg border transition-all duration-150 transform active:scale-95 cursor-pointer ${
                  isGlobalLight 
                    ? "bg-rose-50 hover:bg-rose-100 border-rose-200 text-[#E85D75]" 
                    : isGlobalDark 
                      ? "bg-[#1c1c1c] hover:bg-rose-950/20 border-[#E85D75]/25 text-[#E85D75] hover:border-[#E85D75]/40"
                      : "bg-[#112240] hover:bg-[#E85D75]/20 border-[#E85D75]/25 text-[#E85D75]"
                }`}
                aria-label="Clear Active Chat"
                id="topbar-clear-chat-btn"
              >
                <Trash2 className="w-3.5 h-3.5 shrink-0" />
              </button>
              <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded bg-[#10121A] border border-neutral-700/60 px-2 py-0.5 text-[8.5px] font-bold text-white opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 shadow-xl z-50">
                Clear Active Chat
              </span>
            </div>
          )}

        </div>
      </div>

      {/* Floating Glassmorphic Dropdown Drawer for Console Modes & Custom parameters */}
      {isConsoleDropdownOpen && (
        <>
          {/* Clicking backdrop collapses dropdown */}
          <div 
            className="fixed inset-0 bg-transparent z-45" 
            onClick={() => setIsConsoleDropdownOpen(false)} 
          />
          <div className={`absolute left-4 top-14 w-[min(760px,calc(100vw-2rem))] p-3 rounded-2xl shadow-2xl border backdrop-blur-2xl z-50 transition-all duration-200 transform origin-top-left animate-fade-in ${
            isGlobalLight
              ? "bg-white/78 border-white/80 text-slate-900 shadow-[0_24px_80px_rgba(15,23,42,0.18)]"
              : isGlobalDark
                ? "bg-[#111827]/94 border-white/10 text-neutral-100 shadow-black/80"
                : "bg-[#0B2F73]/92 border-[#F6A400]/30 text-slate-100 shadow-black/90"
          }`}>
            <div className="flex items-center justify-between border-b pb-1.5 mb-2 border-[#cbd5e1]/10">
              <div className="flex items-center gap-1.5">
                <Terminal className={`w-3.5 h-3.5 ${textGoldClassName}`} />
                <span className={`text-[9px] font-bold tracking-wider font-mono uppercase ${isGlobalLight ? "text-slate-600" : "text-slate-300"}`}>
Mode selector
                </span>
              </div>
              <button 
                type="button"
                onClick={() => setIsConsoleDropdownOpen(false)}
                className={`text-[9px] font-mono ${isGlobalLight ? "text-slate-500 hover:text-rose-600" : "text-slate-400 hover:text-rose-400"} font-bold cursor-pointer px-1.5 py-0.5 rounded-md hover:bg-white/10`}
              >
Close
              </button>
            </div>

            {/* Premium 3D Modes Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5">
              {modesList.map((m) => {
                const Icon = m.icon;
                const isActive = m.id === currentMode;
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      onModeChange(m.id);
                      setIsConsoleDropdownOpen(false);
                    }}
                    data-theme={globalTheme}
                    data-active={isActive ? "true" : "false"}
                    className={`rpc-mode-dropdown-card flex items-center gap-2 min-h-10 p-2 rounded-xl border text-left cursor-pointer transition-all duration-150 transform hover:-translate-y-0.5 active:scale-[0.98] ${getModeCardShellClass(globalTheme, isActive, "dropdown")}`}
                    id={`dropdown-mode-tab-${m.id}`}
                    title={m.desc}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className={`p-1 rounded-md border transition-colors ${getModeIconShellClass(globalTheme, isActive)}`}>
                        <Icon className="w-3 h-3 shrink-0" />
                      </div>
                      <span className="text-[10px] font-black tracking-tight uppercase truncate">
                        {m.label.replace("RodgersPC ", "")}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Compact view controls only. No Drive/Web/Image telemetry here. */}
            <div
              className={`mt-2.5 pt-2.5 border-t border-[#cbd5e1]/10 flex flex-wrap items-center gap-1.5 text-[9px] font-mono ${
                isGlobalLight ? "text-slate-600" : "text-slate-400"
              }`}
              id="console-dropdown-telemetry"
            >
              {renderThemeControl("App", globalTheme, onChangeGlobalTheme)}
              {renderThemeControl("Chat", chatTheme, onChangeChatTheme)}
              {renderThemeControl("Input", inputTheme, onChangeInputTheme)}
            </div>
          </div>
        </>
      )}

      {/* Direct stream only */}
      <div 
        ref={chatStreamRef}
        onScroll={handleScroll}
        className={`flex-1 overflow-y-auto p-4 ${!inputPanelVisible ? "pb-24" : "pb-6"} space-y-4 chat-window-scrollbar ${chatStreamBgClassName}`} 
        id="chat-stream-window"
        data-chat-theme={chatTheme}
        style={{ scrollbarWidth: "auto" }}
      >
        {!dbLoaded ? (
          <div className="flex flex-col items-center justify-center h-full w-full max-w-sm mx-auto text-center font-sans space-y-4">
            <div className="relative">
              <div className="absolute inset-0 bg-[#F6A400]/10 rounded-full blur-xl animate-pulse scale-150" />
              <RefreshCw className="w-8 h-8 text-[#FFB72B] animate-spin relative" />
            </div>
            <div className="space-y-1.5 z-10 select-none">
              <p className="text-xs uppercase font-mono font-black tracking-widest text-[#FFB72B]">
                RODGERSPC WORKSPACE LOADING
              </p>
              <p className="text-[9px] text-slate-400 font-mono">
                Loading saved workspace, Drive focus, and active chat state...
              </p>
            </div>
          </div>
        ) : (
          <div className={`transition-all duration-500 ease-out ${
            messages.length === 0 
              ? "opacity-100 scale-100 h-full flex flex-col justify-center" 
              : "opacity-0 scale-95 pointer-events-none absolute inset-0 -z-10 h-0 w-0 overflow-hidden"
          }`}>
            <div className="flex flex-col items-center justify-center max-w-3xl mx-auto w-full px-4 py-2 md:py-4 text-center animate-pop-in">
              {/* Pulsing Command Orb Signifier with subtle gold/blue radial glow */}
              <div className="relative mb-2">
                <div className="absolute inset-0 bg-[#F6A400]/10 rounded-full blur-md animate-pulse scale-125" />
                <div className="relative w-10 h-10 rounded-full bg-gradient-to-tr from-[#0B2F73] via-[#0D4196] to-slate-900 border border-[#F6A400]/40 flex items-center justify-center text-[#FFB72B] shadow-[0_0_12px_rgba(246,164,0,0.18)]">
                  <Cpu className="w-4.5 h-4.5 stroke-[1.5]" />
                </div>
              </div>

              {/* Premium Space Grotesk Display Headings with Chrome & Gold Shimmer */}
              <div className="space-y-0.5">
                <h2 className={`font-display text-base md:text-lg font-bold tracking-tight select-none ${isChatLight ? "text-slate-950" : "text-white"}`}>
                  RodgersPC Command Center
                </h2>
                <p className={`text-[9.5px] md:text-[10px] font-sans max-w-xl mx-auto tracking-normal leading-relaxed ${isChatLight ? "text-slate-600" : "text-slate-300"}`}>
                  Repair-first command workspace for bench diagnostics, customer updates, sourcing, exports, Drive records, and RodgersPC business work.
                </p>
              </div>

              {/* Last session restore / cleanup controls */}
              {hasUnclearedChat && onContinueLastChat ? (
                <div className="mt-2.5 w-full max-w-[360px] mx-auto flex items-center justify-center gap-1.5 rpc-last-session-wrap">
                  <button
                    type="button"
                    onClick={onContinueLastChat}
                    className="rpc-last-session-btn inline-flex h-6 flex-1 items-center justify-center gap-1.5 px-2.5 rounded-md bg-gradient-to-r from-amber-500 via-amber-400 to-[#FFB72B] hover:from-amber-600 hover:to-amber-500 text-slate-950 font-black uppercase tracking-wide cursor-pointer shadow-sm transition-all duration-150 transform active:translate-y-[1px] border-t border-[#ffeebf] border-b border-[#a66f00] whitespace-nowrap"
                    title="Restore the last saved chat session"
                    id="last-session-continue-btn"
                  >
                    <RefreshCw className="w-2.5 h-2.5 shrink-0" />
                    <span>Continue Last Session</span>
                    <span className="opacity-75 font-mono">({lastSavedChatCount})</span>
                  </button>

                  {onClearLastChatBackup && (
                    <button
                      type="button"
                      onClick={onClearLastChatBackup}
                      className={`rpc-last-session-clear inline-flex h-6 items-center justify-center gap-1 px-2 rounded-md border font-black uppercase tracking-wide cursor-pointer transition-all duration-150 active:translate-y-[1px] whitespace-nowrap ${
                        isGlobalLight
                          ? "bg-white/80 border-rose-200 text-rose-700 hover:bg-rose-50"
                          : "bg-rose-500/10 border-rose-500/35 text-rose-300 hover:bg-rose-500/20 hover:text-rose-200"
                      }`}
                      title="Delete the saved last session banner"
                      id="last-session-clear-btn"
                    >
                      <Trash2 className="w-2.5 h-2.5 shrink-0" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
              ) : lastChatBackupClean ? (
                <div className={`rpc-last-session-clean mt-2.5 mx-auto inline-flex h-6 items-center justify-center gap-1.5 px-3 rounded-md border font-black uppercase tracking-wide ${
                  isGlobalLight
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-emerald-500/10 border-emerald-500/25 text-emerald-300"
                }`}>
                  <CheckCircle2 className="w-3 h-3 shrink-0" />
                  Last Session Clean
                </div>
              ) : null}

              {/* Empty-state premium mode cards. No redundant banner above them. */}
              <div className="mt-6 w-full max-w-5xl mx-auto text-left rpc-empty-state-panel">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  {modesList.map((m) => {
                    const Icon = m.icon;
                    const isActive = m.id === currentMode;
                    return (
                      <button
                        key={`empty-mode-card-${m.id}`}
                        type="button"
                        onClick={() => onModeChange(m.id)}
                        data-active={isActive ? "true" : "false"}
                        data-theme={chatTheme}
                        className={`rpc-empty-mode-card group flex min-h-[132px] flex-col justify-between rounded-2xl border p-4 text-left transition-all duration-150 active:scale-[0.99] cursor-pointer ${getModeCardShellClass(chatTheme, isActive, "empty")}`}
                        title={m.desc}
                        id={`empty-mode-card-${m.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-xl border ${getModeIconShellClass(chatTheme, isActive)}`}>
                            <Icon className="w-3.5 h-3.5 shrink-0" />
                          </span>
                          <span className="rpc-empty-mode-title min-w-0 flex-1 text-[9px] font-black uppercase tracking-[0.03em] leading-snug">
                            {m.label.replace("RodgersPC ", "")}
                          </span>
                        </div>
                        <div className={`rpc-empty-mode-desc mt-4 text-[9.5px] leading-relaxed ${getModeDescriptionClass(chatTheme)}`}>
                          {getEmptyModeCardCopy(m.id)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
          </div>
        </div>
      )}

        <div className={`transition-all duration-500 ease-out ${
          messages.length > 0 
            ? "opacity-100 flex-1 flex flex-col space-y-4 font-sans" 
            : "opacity-0 pointer-events-none absolute inset-0 -z-10 h-0 w-0 overflow-hidden"
        }`}>
          {messages.map((msg) => {
            if (msg.isModeChangeIndicator) {
              const displayLabel = (msg.prevMode && msg.newMode && msg.prevMode !== msg.newMode)
                ? `Mode changed: ${getModeLabel(msg.prevMode)} ➜ ${getModeLabel(msg.newMode)}`
                : `Mode shifted to: ${msg.newMode ? getModeLabel(msg.newMode) : msg.content}`;
              return (
                <div key={msg.id} className="flex items-center justify-center my-6 w-full shrink-0 animate-fade-in" id={`mode-change-indicator-${msg.id}`}>
                  <div className={`px-5 py-1.5 rounded-full border text-[9.5px] font-mono uppercase tracking-wider font-extrabold flex items-center gap-2.5 transition-all ${
                    isChatLight 
                      ? "bg-amber-50/70 border-slate-300 text-slate-700 shadow-sm" 
                      : "bg-[#051126]/90 border-[#F6A400]/30 text-[#FFB72B] shadow-md shadow-[#000000]/60"
                  }`}>
                    <Cpu className="w-3.5 h-3.5 text-amber-500 shrink-0 animate-pulse" />
                    <span>{displayLabel}</span>
                  </div>
                </div>
              );
            }

            const isUser = msg.role === "user";
            const messageVisualUrl = getRenderableMessageVisualUrl(msg);
            return (
              <div 
                key={msg.id} 
                className={`flex gap-3 max-w-5xl w-full ${isUser ? "ml-auto justify-end" : "mr-auto justify-start"}`}
                id={`chat-message-${msg.id}`}
              >
                {/* User vs. System Avatar */}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border uppercase font-mono text-xs ${
                  isUser 
                    ? (isChatLight ? "bg-amber-50 border border-amber-200 text-amber-800 order-2" : "bg-[#051126] border-[#112240] text-slate-200 order-2") 
                    : "bg-[#051126] border-[#d4af37]/45 text-[#d4af37]"
                }`}>
                  {isUser ? <UserCheck className="w-3.5 h-3.5" /> : <Flame className="w-3.5 h-3.5 text-[#d4af37]" />}
                </div>

                {/* Bubble content */}
                <div className={`space-y-1.5 min-w-0 flex-1 ${isUser ? "max-w-2xl order-1 text-right" : "max-w-3xl text-left"}`}>
                  <div className={`inline-flex items-center gap-1.5 text-[9px] font-mono ${isChatLight ? "text-slate-400" : "text-slate-600"} ${isUser ? "mr-1 justify-end" : "ml-1 justify-start"}`}>
                    <span>{msg.timestamp}</span>
                    {!isUser && (
                      <span className={`rounded-full border px-1.5 py-0.5 text-[7.5px] font-black uppercase tracking-wider ${isChatLight ? "border-slate-200 bg-white/70 text-[#143F8F]" : "border-[#F6A400]/25 bg-[#F6A400]/8 text-[#FFB72B]"}`}>
                        {getMessageModeLabel(msg, currentMode)}
                      </span>
                    )}
                  </div>

                  <div className={`p-3 rounded-xl shadow-sm border text-[11px] leading-relaxed ${
                    isUser 
                      ? userBubbleStyle 
                      : assistantBubbleStyle
                  }`} id={`content-bubble-${msg.id}`}>
                    <div className="space-y-2 max-w-none text-left font-sans">
                      {isUser ? (
                        <span className={`whitespace-pre-wrap ${isChatLight ? "text-slate-900 font-medium" : "text-slate-200 font-medium"}`}>{msg.content}</span>
                      ) : editingMessageId === msg.id ? (
                        <div className="space-y-2.5 w-full mt-1">
                          <textarea
                            className={`w-full p-2.5 rounded-lg text-xs font-mono leading-relaxed border min-h-[140px] focus:outline-none focus:ring-1 ${
                              isChatLight 
                                ? "bg-slate-50 border-slate-300 text-slate-900 focus:ring-amber-500" 
                                : "bg-neutral-900 border-neutral-700 text-neutral-100 focus:ring-amber-500"
                            }`}
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                          />
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              onClick={() => {
                                if (onUpdateMessage) onUpdateMessage(msg.id, editingText);
                                setEditingMessageId(null);
                              }}
                              className="px-2.5 py-1 text-[9.5px] font-bold font-sans uppercase rounded text-white bg-emerald-600 hover:bg-emerald-500 transition-colors cursor-pointer"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingMessageId(null)}
                              className="px-2.5 py-1 text-[9.5px] font-bold font-sans uppercase rounded text-slate-300 bg-neutral-800 hover:bg-neutral-800 transition-colors cursor-pointer border border-neutral-700/40"
                            >
                              Cancel
                            </button>
                            {msg.originalContent !== undefined && msg.originalContent !== msg.content && (
                              <button
                                onClick={() => setEditingText(msg.originalContent || "")}
                                className="px-2.5 py-1 text-[9.5px] font-bold font-sans uppercase rounded text-amber-500 hover:text-amber-400 bg-amber-950/20 hover:bg-amber-950/45 border border-amber-500/20 transition-colors cursor-pointer"
                              >
                                Reset Original
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <PremiumMarkdownContent text={msg.content} isLightTheme={isChatLight} />
                      )}

                      {/* Render multiple msg attachments (if available) */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {msg.attachments.map((att) => {
                            const previewSrc = getRenderableAttachmentDataUrl(att);
                            const isImg =
                              Boolean(previewSrc) ||
                              att.type === "image" ||
                              Boolean(att.mimeType?.startsWith("image/"));
                            const isFakeVisual = isFakeOrTruncatedVisualPayload(att.base64) || isFakeOrTruncatedVisualPayload(att.content);

                            if (isImg && previewSrc) {
                              return (
                                <div key={att.id} className="relative rounded-lg overflow-hidden border border-slate-500/10 max-w-sm bg-slate-900 shadow">
                                  <img 
                                    src={previewSrc} 
                                    alt={att.name} 
                                    className="max-h-72 object-contain w-full" 
                                    referrerPolicy="no-referrer"
                                  />
                                  <div className="absolute bottom-0 inset-x-0 bg-black/60 p-1 text-[8px] font-mono text-slate-300 truncate">
                                    {att.name}
                                  </div>
                                </div>
                              );
                            }

                            if (isImg && !previewSrc) {
                              return (
                                <div key={att.id} className={`p-2 border rounded-lg flex items-center justify-between max-w-sm gap-4 ${isChatLight ? "bg-amber-50 border-amber-200 text-amber-950" : "bg-amber-950/15 border-amber-500/25 text-amber-200"}`}>
                                  <div className="flex items-center gap-2 min-w-0 font-sans">
                                    <AlertTriangle className="w-3 h-3 shrink-0 text-amber-500" />
                                    <div className="min-w-0 text-left">
                                      <p className="text-[10px] font-mono font-bold truncate max-w-[150px]">{att.name || "Image attachment"}</p>
                                      <p className="text-[7.5px] font-mono opacity-70 uppercase">
                                        {isFakeVisual ? "Image payload was truncated / not visual evidence" : "Image preview unavailable"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            return (
                              <div key={att.id} className={`p-2 border rounded-lg flex items-center justify-between max-w-sm gap-4 ${isChatLight ? "bg-stone-50 border-stone-200 text-stone-900" : "bg-[#051126]/60 border-neutral-800 text-neutral-300"}`}>
                                <div className="flex items-center gap-2 min-w-0 font-sans">
                                  <FileText className={`w-3 h-3 shrink-0 ${att.type === "pdf" ? "text-red-500" : att.type === "spreadsheet" ? "text-emerald-500" : "text-blue-500"}`} />
                                  <div className="min-w-0 text-left">
                                    <p className="text-[10px] font-mono font-bold truncate max-w-[150px]">{att.name}</p>
                                    <p className="text-[7.5px] font-mono opacity-60 uppercase">
                                      {[
                                        att.type,
                                        (att as any).extractionStatus ? `extraction:${(att as any).extractionStatus}` : "",
                                        (att as any).hasReadableText ? "text:ready" : "",
                                        (att as any).isBinaryPayload ? "binary:sent" : "",
                                      ].filter(Boolean).join(" · ")}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    let blob: Blob;
                                    if (att.content && att.content.startsWith("data:")) {
                                      blob = await fetch(att.content).then((r) => r.blob());
                                    } else {
                                      blob = new Blob([att.content || ""], { type: "text/plain;charset=utf-8" });
                                    }
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = att.name || "RodgersPC_Attachment";
                                    link.click();
                                    URL.revokeObjectURL(url);
                                  }}
                                  className="text-[8.5px] font-mono font-bold px-1.5 py-0.5 rounded border border-slate-500/20 hover:text-[#d4af37] transition-all cursor-pointer select-none"
                                  title="Download attachment"
                                >
                                  [DL]
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {messageVisualUrl && (() => {
                        const imagePayload = String(messageVisualUrl || "");
                        const isPdf = imagePayload.startsWith("data:application/pdf") && isRenderableDataUrl(imagePayload);
                        const canRenderImagePayload = isRenderableDataUrl(imagePayload) || isRenderableRemoteImageSrc(imagePayload);
                        const isOptimized = isFakeOrTruncatedVisualPayload(imagePayload) || !canRenderImagePayload;
                        
                        if (isOptimized) {
                          return (
                            <div className={`mt-3 p-3 border rounded-lg flex items-center gap-2.5 max-w-sm ${isChatLight ? "bg-amber-50 border-amber-200 text-amber-950" : "bg-amber-950/15 border-amber-500/25 text-amber-200"}`}>
                              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-mono font-bold">Visual Payload Not Retained</p>
                                <p className="text-[10px] font-mono opacity-70">This is not usable screenshot evidence and will not be exported as an image.</p>
                              </div>
                            </div>
                          );
                        }
                        
                        if (isPdf) {
                          return (
                            <div className={`mt-3 p-3 border rounded-lg flex items-center justify-between max-w-sm ${isChatLight ? "bg-red-50 border-red-200 text-red-950" : "bg-red-950/20 border-red-900/40 text-red-100"}`}>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <FileText className="w-5 h-5 text-red-500 shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-xs font-mono font-bold truncate">Attached PDF Document</p>
                                  <p className="text-[10px] font-mono opacity-60">Loaded as inline technical manual</p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div className={`mt-3.5 border rounded-lg overflow-hidden bg-slate-905 max-w-sm ${chatBorderClassName}`}>
                            <div className="relative group bg-neutral-950 flex items-center justify-center">
                              <img 
                                src={messageVisualUrl} 
                                alt="Generated or attached visual" 
                                className="w-full object-contain max-h-80" 
                                referrerPolicy="no-referrer"
                              />
                            </div>
                            
                            {/* Rich controls area for copying, resizing and downloading */}
                            <div className={`p-2 font-mono border-t flex flex-col gap-2 ${isChatLight ? "bg-stone-50 border-stone-200 text-stone-700" : "bg-[#093375]/40 border-[#F6A400]/15 text-slate-350"}`}>
                              <div className="flex items-center justify-between text-[10px] pb-1 border-b border-white/5 font-extrabold">
                                <span>{msg.role === "user" ? "Attached Image" : "Generated Visual"}</span>
                                <button 
                                  onClick={async () => {
                                    if (messageVisualUrl) {
                                      const success = await copyImageToClipboard(messageVisualUrl);
                                      if (success) {
                                        setCopiedImgId(msg.id);
                                        setTimeout(() => setCopiedImgId(null), 2000);
                                      }
                                    }
                                  }}
                                  className={`px-2 py-0.5 rounded text-[8.5px] border cursor-pointer font-black uppercase transition-all duration-100 ${
                                    isGlobalLight 
                                      ? "bg-[#143F8F]/10 border-[#143F8F]/25 text-[#143F8F] hover:bg-[#143F8F]/20" 
                                      : isGlobalDark 
                                        ? "bg-[#F6A400]/10 border-[#F6A400]/25 text-[#F6A400] hover:bg-[#F6A400]/20" 
                                        : "bg-[#FFB72B]/15 border border-[#FFB72B]/30 text-[#FFB72B] hover:bg-[#FFB72B]/25"
                                  }`}
                                  title="Copy image binary to clipboard"
                                >
                                  {copiedImgId === msg.id ? "[Copied Image]" : "Copy Image"}
                                </button>
                              </div>

                              <div className="flex flex-col gap-1.5 pt-0.5">
                                <span className={`text-[8px] font-bold ${
                                  isGlobalLight ? "text-slate-500" : isGlobalDark ? "text-neutral-400" : "text-slate-300"
                                }`}>IMAGE EXPORT:</span>
                                <div className="flex flex-wrap gap-1">
                                  <button
                                    onClick={() => messageVisualUrl && downloadResizedImage(messageVisualUrl, "original")}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      isGlobalLight 
                                        ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                        : isGlobalDark 
                                          ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                          : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Download standard original dimension size"
                                  >
                                    Original
                                  </button>
                                  <button
                                    onClick={() => messageVisualUrl && downloadResizedImage(messageVisualUrl, 0.5)}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      isGlobalLight 
                                        ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                        : isGlobalDark 
                                          ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                          : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Downscale to 50% width and height"
                                  >
                                    50% Web
                                  </button>
                                  <button
                                    onClick={() => messageVisualUrl && downloadResizedImage(messageVisualUrl, 1280)}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      isGlobalLight 
                                        ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                        : isGlobalDark 
                                          ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                          : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Save optimized for 1280px standard layout size"
                                  >
                                    720p HD
                                  </button>
                                  <button
                                    onClick={() => messageVisualUrl && downloadResizedImage(messageVisualUrl, 1920)}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      isGlobalLight 
                                        ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                        : isGlobalDark 
                                          ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                          : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Save as crisp high-resolution 1920px widescreen standard"
                                  >
                                    1080p FHD
                                  </button>
                                  <button
                                    onClick={() => messageVisualUrl && downloadResizedImage(messageVisualUrl, 300)}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      isGlobalLight 
                                        ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                        : isGlobalDark 
                                          ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                          : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Rescale to square 300px thumbnail file representation"
                                  >
                                    300px
                                  </button>

                                  <button
                                    onClick={() => {
                                      setActiveCustomResizeMsgId(activeCustomResizeMsgId === msg.id ? null : msg.id);
                                    }}
                                    className={`px-1.5 py-0.5 text-[8px] border rounded font-black uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                                      activeCustomResizeMsgId === msg.id
                                        ? "bg-[#F6A400] text-black border-[#F6A400]"
                                        : isGlobalLight 
                                          ? "bg-slate-100 border-slate-250 text-[#143F8F] hover:bg-slate-200" 
                                          : isGlobalDark 
                                            ? "bg-neutral-800 border-neutral-700 text-[#F6A400] hover:bg-neutral-700" 
                                            : "bg-[#143F8F]/85 border-[#F6A400]/30 text-[#FFB72B] hover:bg-[#194db3]"
                                    }`}
                                    title="Display form inputs for customizable dimension scaling"
                                  >
                                    Custom Resize...
                                  </button>
                                </div>

                                {activeCustomResizeMsgId === msg.id && (
                                  <div className={`mt-2 p-3 border rounded-lg space-y-2 text-[10px] font-mono leading-tight shadow-md ${
                                    isGlobalLight ? "bg-slate-100 border-slate-300 text-slate-800" : "bg-neutral-900/90 border-[#F6A400]/25 text-neutral-300"
                                  }`}>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Width</label>
                                        <input 
                                          type="number" 
                                          className="w-full px-1.5 py-0.5 rounded border border-slate-300/30 bg-black/30 text-inherit text-[10px] text-center" 
                                          value={customWidth}
                                          onChange={(e) => setCustomWidth(Math.max(1, parseInt(e.target.value) || 1))}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Height</label>
                                        <input 
                                          type="number" 
                                          className="w-full px-1.5 py-0.5 rounded border border-slate-300/30 bg-black/30 text-inherit text-[10px] text-center" 
                                          value={customHeight}
                                          onChange={(e) => setCustomHeight(Math.max(1, parseInt(e.target.value) || 1))}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Unit</label>
                                        <select 
                                          className="w-full px-1 py-0.5 rounded border border-slate-300/30 bg-black/35 text-inherit text-[9px]"
                                          value={customUnit}
                                          onChange={(e) => {
                                            const val = e.target.value as any;
                                            setCustomUnit(val);
                                            if (val === "inches") {
                                              setCustomWidth(6);
                                              setCustomHeight(4);
                                            } else {
                                              setCustomWidth(1348);
                                              setCustomHeight(1020);
                                            }
                                          }}
                                        >
                                          <option value="pixels" className="bg-slate-900 text-white">Pixels</option>
                                          <option value="inches" className="bg-slate-900 text-white">Inches</option>
                                        </select>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Fit Mode</label>
                                        <select 
                                          className="w-full px-1 py-0.5 rounded border border-slate-300/30 bg-black/35 text-inherit text-[9px]"
                                          value={customFit}
                                          onChange={(e) => setCustomFit(e.target.value as any)}
                                        >
                                          <option value="contain" className="bg-slate-900 text-white">Contain (Fit In)</option>
                                          <option value="cover" className="bg-slate-900 text-white">Cover (Crop Fill)</option>
                                          <option value="stretch" className="bg-slate-900 text-white">Stretch (Skew)</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-bold text-slate-400 uppercase mb-0.5">Background</label>
                                        <select 
                                          className="w-full px-1 py-0.5 rounded border border-slate-300/30 bg-black/35 text-inherit text-[9px]"
                                          value={customBg}
                                          onChange={(e) => setCustomBg(e.target.value as any)}
                                        >
                                          <option value="blur" className="bg-slate-900 text-white">Blur (Ambient Fill)</option>
                                          <option value="transparent" className="bg-slate-900 text-white">Transparent</option>
                                          <option value="white" className="bg-slate-900 text-white">White Page</option>
                                          <option value="black" className="bg-slate-900 text-white">Black Page</option>
                                        </select>
                                      </div>
                                    </div>

                                    <button
                                      onClick={() => messageVisualUrl && downloadResizedCustom(messageVisualUrl, customWidth, customHeight, customUnit, customFit, customBg)}
                                      className="w-full py-1 text-center font-bold text-[9px] uppercase border rounded-md cursor-pointer text-white bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 border-amber-500 transition-all font-sans"
                                    >
                                      Apply & Download Resize
                                    </button>
                                  </div>
                                )}

                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Highly polished, scannable custom Copy, Edit & Download action items inside bubbles as tooltip-only headers */}
                    <div className={`mt-3.5 pt-2 border-t flex items-center justify-end gap-3 text-[9.5px] font-mono select-none ${
                      isChatLight ? "border-slate-200 text-slate-400" : "border-white/10 text-slate-500"
                    }`}>
                      {/* Retry failed assistant response */}
                      {!isUser && onRetryMessage && isRetryableAssistantFailure(msg) && (
                        <div className="relative group/tooltip flex items-center">
                          <button
                            type="button"
                            onClick={() => onRetryMessage(msg.id)}
                            className={`rpc-retry-btn p-1.5 rounded-md transition-all duration-100 cursor-pointer flex items-center justify-center border ${
                              isChatLight
                                ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 hover:border-amber-300"
                                : "bg-amber-500/12 text-[#FFB72B] border-[#F6A400]/30 hover:bg-amber-500/20 hover:border-[#F6A400]/50"
                            }`}
                            aria-label="Retry this failed request"
                          >
                            <RefreshCw className="w-3 h-3 shrink-0" />
                          </button>
                          <span className="hidden">
                            Retry Exact Request
                          </span>
                        </div>
                      )}

                      {/* Edit Button */}
                      {!isUser && (
                        <div className="relative group/tooltip flex items-center">
                          <button
                            onClick={() => {
                              setEditingMessageId(msg.id);
                              setEditingText(msg.content);
                            }}
                            className={`p-1.5 rounded-md hover:bg-white/5 transition-all duration-100 cursor-pointer flex items-center justify-center border border-transparent ${
                              isChatLight ? "hover:text-slate-900 hover:bg-slate-100 hover:border-slate-200" : "hover:text-[#F6A400] hover:border-white/10"
                            }`}
                            aria-label="Edit generated output response text"
                          >
                            <Wrench className="w-3 h-3 shrink-0" />
                          </button>
                          <span className="hidden">
                            Edit Response Text
                          </span>
                        </div>
                      )}

                      {/* Copy Button */}
                      <div className="relative group/tooltip flex items-center">
                        <button
                          onClick={() => handleCopyText(`msg_${msg.id}`, msg.content)}
                          className={`p-1.5 rounded-md hover:bg-white/5 transition-all duration-100 cursor-pointer flex items-center justify-center border border-transparent ${
                            isChatLight ? "hover:text-slate-900 hover:bg-slate-100 hover:border-slate-200" : "hover:text-[#F6A400] hover:border-white/10"
                          }`}
                          id={`copy-bubble-btn-${msg.id}`}
                          aria-label="Copy message content"
                        >
                          {copiedId === `msg_${msg.id}` ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#2FD6A3] shrink-0 animate-pulse" />
                          ) : (
                            <Copy className="w-3 h-3 shrink-0" />
                          )}
                        </button>
                        <span className="hidden">
                          {copiedId === `msg_${msg.id}` ? "Copied!" : "Copy to Clipboard"}
                        </span>
                      </div>

                      {/* Download TXT Button */}
                      <div className="relative group/tooltip flex items-center">
                        <button
                          onClick={() => handleDownloadFile(isUser ? "user_prompt" : "system_response", msg.content, "txt")}
                          className={`p-1.5 rounded-md hover:bg-white/5 transition-all duration-100 cursor-pointer flex items-center justify-center border border-transparent ${
                            isChatLight ? "hover:text-slate-900 hover:bg-slate-100 hover:border-slate-200" : "hover:text-[#F6A400] hover:border-white/10"
                          }`}
                          id={`dl-bubble-btn-${msg.id}`}
                          aria-label="Download as a text file"
                        >
                          <Download className="w-3 h-3 shrink-0" />
                        </button>
                        <span className="hidden">
                          Download TXT Response
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Operational Terminal Commands Blocks (Fully visible executable scripts) */}
                  {!isUser && msg.commands && msg.commands.length > 0 && (
                    <div className="space-y-2 mt-2.5 text-left">
                      <span className="text-[9px] font-semibold text-slate-500 font-mono tracking-wider uppercase block">
                        Command blocks
                      </span>
                      <div className="grid gap-2">
                        {msg.commands.map((cmd) => {
                          const isPowershell = cmd.type === "powershell_single";
                          return (
                            <div 
                              key={cmd.id} 
                              className={`border rounded-2xl overflow-hidden shadow-sm ${
                                isChatLight
                                  ? "bg-white border-slate-200"
                                  : "bg-[#0B1220] border-white/10"
                              }`}
                              id={`command-card-${cmd.id}`}
                            >
                              <div className={`px-3 py-1.5 border-b flex items-center justify-between ${isChatLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.035] border-white/10"}`}>
                                <span className={`rpc-shell-badge inline-flex items-center rounded-lg border px-2 py-0.5 font-mono text-[9px] font-black uppercase tracking-[0.11em] ${
                                  isChatLight
                                    ? "border-[#143F8F]/30 bg-gradient-to-b from-white to-slate-100 text-[#143F8F] shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]"
                                    : "border-[#F6A400]/55 bg-gradient-to-b from-[#122444]/96 to-[#071226]/96 text-[#FFB72B] shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]"
                                }`}>
                                  {isPowershell ? "PowerShell" : "Command"}
                                </span>
                                <span className="text-[10px] font-mono text-slate-500 truncate max-w-xs">{cmd.title}</span>
                              </div>
                              <pre className={`p-3 font-mono text-[11.5px] overflow-x-auto whitespace-pre select-all leading-6 ${isChatLight ? "text-slate-900 bg-white" : "text-slate-100 bg-[#070D18]"}`}>
                                {cmd.code}
                              </pre>
                              <div className={`px-3 py-1.5 flex justify-end border-t ${isChatLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.025] border-white/10"}`}>
                                <button
                                  onClick={() => handleCopyText(`cmd_${cmd.id}`, cmd.code)}
                                  className={`flex items-center gap-1.5 text-[10px] font-mono font-semibold uppercase tracking-wide cursor-pointer ${isChatLight ? "text-slate-500 hover:text-[#143F8F]" : "text-slate-400 hover:text-[#F6A400]"}`}
                                  id={`btn-copy-cmd-${cmd.id}`}
                                >
                                  {copiedId === `cmd_${cmd.id}` ? (
                                    <>
                                      <Check className="w-3 h-3 text-emerald-500" /> Copied
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-3 h-3" /> Copy
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Reports are intentionally not rendered in normal chat. Use Notes or Report Studio when needed. */}
                </div>
              </div>
            );
          })}
        </div>

        {/* Live model progress / troubleshooting state */}
        {loading && (
          <div className="flex items-center justify-center px-4 py-3">
            {!diagnosticsExpanded ? (
              <div
                className={`flex w-full max-w-[680px] items-center justify-between gap-3 rounded-2xl border px-3.5 py-2.5 backdrop-blur-xl shadow-xl ${
                  isChatLight
                    ? "border-slate-300/80 bg-white/78 text-slate-900 shadow-slate-300/30"
                    : "border-[#F6A400]/24 bg-[#020B1E]/78 text-slate-100 shadow-[0_18px_70px_rgba(0,0,0,0.45)]"
                }`}
                id="pipeline-monitor-compact"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#F6A400]/35 bg-[#F6A400]/10">
                    <RefreshCw className="h-4 w-4 animate-spin text-[#FFCE73]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-mono text-[9px] font-black uppercase tracking-[0.13em]">
                      <span className="text-[#F6A400]">Working</span>
                      <span className={`rounded-full border px-1.5 py-0.5 text-[8px] ${isChatLight ? "border-slate-300 bg-white text-slate-500" : "border-white/10 bg-black/24 text-slate-400"}`}>
                        {requestElapsedSeconds}s
                      </span>
                    </div>
                    <div className={`mt-0.5 truncate font-mono text-[9px] ${isChatLight ? "text-slate-500" : "text-slate-400"}`}>
                      {String(currentProgressStep || "Processing request...").replace(/^[-•>\s]+/, "")}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setDiagnosticsExpanded(true)}
                    className={`h-7 rounded-lg border px-2.5 font-mono text-[8px] font-black uppercase tracking-[0.08em] transition ${
                      isChatLight
                        ? "border-slate-300 bg-white text-[#143F8F] hover:bg-slate-50"
                        : "border-[#F6A400]/28 bg-[#F6A400]/10 text-[#FFCE73] hover:bg-[#F6A400]/16"
                    }`}
                    id="pipeline-monitor-show-diagnostics"
                    title="Show diagnostic trace"
                  >
                    Diagnostics
                  </button>
                  {onCancelRequest && (
                    <button
                      type="button"
                      onClick={onCancelRequest}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-rose-500/40 bg-rose-950/30 text-rose-200 shadow-[0_0_18px_rgba(244,63,94,0.16)] transition-all duration-200 hover:scale-110 hover:bg-rose-950/55 active:scale-95"
                      title="Cancel response"
                      id="workspace-cancel-btn-compact"
                    >
                      <X className="h-3.5 w-3.5 stroke-[3px]" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={`relative w-full max-w-[920px] overflow-hidden rounded-2xl border backdrop-blur-xl shadow-2xl ${
                  isChatLight
                    ? "border-slate-300/80 bg-white/72 text-slate-950 shadow-slate-300/45"
                    : "border-[#F6A400]/35 bg-[#020B1E]/78 text-slate-100 shadow-[0_24px_90px_rgba(0,0,0,0.55)]"
                }`}
                id="pipeline-monitor-diagnostics"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,164,0,0.20),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.13),transparent_38%)]" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F6A400] to-transparent" />

                <div
                  className={`relative flex items-center justify-between gap-3 border-b px-4 py-3 ${
                    isChatLight
                      ? "border-slate-200/80 bg-white/58"
                      : "border-white/10 bg-white/[0.035]"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#F6A400]/35 bg-[#F6A400]/10 shadow-[0_0_26px_rgba(246,164,0,0.16)]">
                      <div className="absolute inset-1 rounded-lg border border-white/10" />
                      <Terminal className="h-4 w-4 text-[#FFCE73]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-[0.16em]">
                        <span className="truncate text-[#F6A400]">RodgersPC Diagnostic Monitor</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[8.5px] ${isChatLight ? "border-slate-300 bg-slate-100 text-slate-600" : "border-white/10 bg-black/24 text-slate-400"}`}>
                          LIVE TRACE
                        </span>
                      </div>
                      <div className={`mt-1 font-mono text-[9px] ${isChatLight ? "text-slate-500" : "text-slate-400"}`}>
                        Copyable request trace for hangs, rejected candidates, tool routing, timing, and server/model handoff.
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <div className={`rounded-xl border px-3 py-1.5 text-right font-mono ${isChatLight ? "border-slate-300 bg-white/70" : "border-[#F6A400]/20 bg-black/24"}`}>
                      <div className={`text-[7px] font-black uppercase tracking-[0.14em] ${isChatLight ? "text-slate-400" : "text-slate-500"}`}>elapsed</div>
                      <div className="text-[12px] font-black text-[#FFCE73]">{requestElapsedSeconds}s</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopyDiagnosticLog}
                      className={`h-8 rounded-xl border px-3 font-mono text-[8px] font-black uppercase tracking-[0.08em] transition ${
                        copiedDiagnosticLog
                          ? "border-emerald-400/50 bg-emerald-500/14 text-emerald-200"
                          : isChatLight
                            ? "border-slate-300 bg-white text-[#143F8F] hover:bg-slate-50"
                            : "border-[#F6A400]/28 bg-[#F6A400]/10 text-[#FFCE73] hover:bg-[#F6A400]/16"
                      }`}
                      id="pipeline-monitor-copy-diagnostics"
                      title="Copy full diagnostic trace"
                    >
                      {copiedDiagnosticLog ? "Copied" : "Copy Log"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiagnosticsExpanded(false)}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
                        isChatLight
                          ? "border-slate-300 bg-white text-slate-500 hover:text-slate-900"
                          : "border-white/10 bg-white/[0.04] text-slate-400 hover:text-white"
                      }`}
                      title="Hide diagnostics"
                      id="pipeline-monitor-hide-diagnostics"
                    >
                      <ChevronDown className="h-3.5 w-3.5 stroke-[3px]" />
                    </button>
                    {onCancelRequest && (
                      <button
                        type="button"
                        onClick={onCancelRequest}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-rose-500/40 bg-rose-950/30 text-rose-200 shadow-[0_0_18px_rgba(244,63,94,0.16)] transition-all duration-200 hover:scale-110 hover:bg-rose-950/55 active:scale-95"
                        title="Cancel response"
                        id="workspace-cancel-btn-expanded"
                      >
                        <X className="h-3.5 w-3.5 stroke-[3px]" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="relative px-4 py-3.5">
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[length:100%_22px] opacity-25" />
                  <div className="relative max-h-[240px] overflow-y-auto rounded-xl border border-white/10 bg-black/26 p-3 font-mono text-[11px] leading-5 shadow-inner">
                    <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-[#F6A400] via-cyan-400 to-emerald-400" />
                    <div className="space-y-1.5 pl-2">
                      {visibleProgressSteps.slice(-18).map((step, index, visibleList) => {
                        const isActiveLine = index === visibleList.length - 1;
                        const cleanedStep = String(step || "")
                          .replace(/^[-•>\s]+/, "")
                          .replace(/^Server:\s*/i, "srv ▸ ")
                          .replace(/^Client:\s*/i, "ui ▸ ")
                          .trim();
                        const lineAge = Math.max(0, requestElapsedSeconds - (visibleList.length - index - 1));
                        const isRejected = /rejected|failed|blocked|wrong|unreadable|skipped|not verified|timeout|error/i.test(cleanedStep);
                        const isAccepted = /accepted|readable|received|finished|selected|matched|verified|responded|complete/i.test(cleanedStep);
                        const bulletClass = isRejected
                          ? "text-rose-300"
                          : isActiveLine
                            ? "text-[#F6A400]"
                            : isAccepted
                              ? "text-emerald-300"
                              : "text-cyan-300";
                        const rowClass = isActiveLine
                          ? "border-[#F6A400]/30 bg-[#F6A400]/8 text-[#FFEEBF] shadow-[0_0_22px_rgba(246,164,0,0.06)]"
                          : isRejected
                            ? "border-rose-400/15 bg-rose-500/6 text-rose-100"
                            : isAccepted
                              ? "border-emerald-400/15 bg-emerald-500/6 text-emerald-50"
                              : "border-white/5 bg-white/[0.025] text-slate-300";
                        return (
                          <div key={`${step}-${index}`} className={`group flex items-start gap-2 rounded-lg border px-2.5 py-1.5 transition-all ${rowClass}`}>
                            <span className={`mt-[1px] select-none font-black ${bulletClass}`}>
                              {isActiveLine ? "▸" : isRejected ? "×" : isAccepted ? "✓" : "•"}
                            </span>
                            <span className="mt-[1px] w-[46px] shrink-0 select-none rounded border border-white/10 bg-black/20 px-1.5 text-center text-[9px] font-black text-slate-400">
                              {String(lineAge).padStart(2, "0")}s
                            </span>
                            <span className="min-w-0 flex-1 break-words">
                              {cleanedStep}
                              {isActiveLine && <span className="ml-1 inline-block h-3 w-1.5 translate-y-[2px] animate-pulse bg-[#F6A400]" />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}      </div>

      {/* Styled Floating Scroll-To-Bottom circle activator button */}
      {showScrollBottomBtn && (
        <button
          type="button"
          onClick={scrollToBottom}
          className={`absolute bottom-6 right-6 p-2 rounded-full border shadow-2xl transition-all duration-200 transform scale-100 hover:scale-110 active:scale-95 z-50 cursor-pointer ${
            isGlobalLight 
              ? "bg-amber-500 hover:bg-amber-600 border-amber-400 text-white shadow-amber-500/30" 
              : isGlobalDark 
                ? "bg-teal-500 hover:bg-teal-600 border-teal-400 text-neutral-950 shadow-teal-500/35" 
                : "bg-gradient-to-b from-[#eac558] to-[#b38f1a] hover:opacity-95 border-[#ffdf7a] text-[#020b1e] shadow-[#d4af37]/45"
          }`}
          title="Scroll instantly to bottom"
          id="scroll-to-bottom-circle-btn"
        >
          <ChevronDown className="w-5 h-5 stroke-[3px]" />
        </button>
      )}
    </div>
  );
}
